import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';
import { useDebounceFn, useMediaQuery } from '@vueuse/core';
import {
  CANDIDATE_COLUMNS,
  COLUMN_BY_KEY,
  DEFAULT_VISIBLE_COLUMNS,
  GROUPABLE_FIELDS,
  viewConfigSchema,
  reconcileRows,
  type CandidateRow,
  type CandidateWorkObjectValue,
  type CandidateWorkObjectPatchResult,
  type CandidateCellBoldSnapshot,
  type CandidateTravelMateChange,
  type HiddenRowGap,
  type CandidateMove,
  type DepartureGroupState,
  type CopyLogPayload,
  type ColumnMeta,
  type BlacklistMatch,
  type CandidateCommentCheck,
  type CandidateCommentMatch,
  type DuplicateMatch,
  type Filter,
  type Sort,
  type GridDensity,
  type BlacklistCheck,
  type PhoneQueryField,
} from '@yana/shared';
import { ApiError, api, newOpId } from '@/api/client';
import { markError, markRemoteChanged, markSaved, markSaving } from '@/lib/cellDecorations';
import { useToastStore } from './toast';
import { useAuthStore } from './auth';
import { useDateGroupVisibilityStore } from './dateGroupVisibility';
import { useUndoStack, type UndoRecorder } from '@/composables/useUndoStack';
import { createBatchHistory, type CandidateHistoryRow } from '@/lib/candidateHistory';
import { dateGroupComparator, type DateGroupOrder } from '@/lib/gridGroups';

/**
 * Состояние грида.
 *
 * Строки хранятся в shallowRef: при 40 колонках и тысячах строк глубокая
 * реактивность Vue съедает больше времени, чем сам рендер. Обновление —
 * замена объекта строки и новая ссылка на массив (см. replaceAt). Ручной
 * triggerRef здесь был и не работал: он будит подписчиков самого ref,
 * а грид читает строки через стор, и до него сигнал не доходил.
 */

const PAGE_SIZE = 200;
/** Сколько строк держим в памяти. Выше — начинаем выбрасывать дальний хвост. */
const MAX_ROWS_IN_MEMORY = 20_000;
const GRID_COLUMNS = CANDIDATE_COLUMNS.filter((column) => !column.internal);
const publicColumn = (key: string) => Boolean(COLUMN_BY_KEY[key] && !COLUMN_BY_KEY[key]!.internal);
/** Даты выводятся отдельными строками снизу, а не выбираются в меню «Вид». */
const VIEW_GROUPABLE_FIELDS = GROUPABLE_FIELDS.filter((field) =>
  !['sectionId', 'addedDate', 'plannedDepartureDate'].includes(field.key));

export interface CellPosition {
  rowIndex: number;
  colKey: string;
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface PendingEdit {
  rowId: string;
  field: string;
  state: SaveState;
}

export interface CellPatchResult {
  ok: boolean;
  previous: unknown;
  version?: number;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

interface CellPatchResponse {
  version: number;
  value: unknown;
  derived: Record<string, unknown>;
  updatedAt: string;
}

export type WorkObjectValue = CandidateWorkObjectValue;
type WorkObjectPatchResponse = CandidateWorkObjectPatchResult;

interface RowPlace {
  anchorId: string | null;
  position: 'before' | 'after';
  groupKey: string | null;
}

const departureState = (row: CandidateRow): DepartureGroupState => ({
  sectionId: row.sectionId ?? null,
  plannedDepartureDate: row.plannedDepartureDate ?? null,
});

/**
 * Правка плановой даты может автоматически переместить строку в группу.
 * Для Ctrl+Z сохраняем оба значения одной операцией: иначе дата вернётся,
 * а прежняя ручная группа потеряется. Ручной перенос группы по-прежнему
 * меняет только sectionId.
 */
export function historyFields(fields: string[]): string[] {
  return [...new Set(fields.includes('plannedDepartureDate')
    ? [...fields, 'sectionId']
    : fields)];
}

function cellSnapshot(row: CandidateRow, field: string): Record<string, unknown> {
  return Object.fromEntries(historyFields([field]).map((key) => [
    key, storedCellValue(row, key) ?? null,
  ]));
}

/** Отмена восстанавливает ручной возраст, включая режим расчёта по дате. */
function storedCellValue(row: CandidateRow, field: string): unknown {
  return field === 'age' ? row.ageOverride ?? null
    : (row as unknown as Record<string, unknown>)[field];
}

export const useGridStore = defineStore('grid', () => {
  const toasts = useToastStore();
  const auth = useAuthStore();
  const dateVisibility = useDateGroupVisibilityStore();
  const history = useUndoStack();
  const sections = ref<Array<{ id: string; date: string; label: string; sortOrder: number; rowCount?: number }>>([]);
  /** Порядок строк дат в нижнем блоке таблицы. */
  const dateSectionOrder = ref<DateGroupOrder>('asc');
  let sectionRequest = 0;

  async function loadSections() {
    const request = ++sectionRequest;
    const path = includeDeleted.value || includeHidden.value || hiddenOnly.value
      ? '/candidates/sections?includeHidden=true'
      : '/candidates/sections';
    const response = await api.get<{ items: typeof sections.value }>(path);
    if (request === sectionRequest) {
      const compare = dateGroupComparator(response.items, [], EMPTY_GROUP_KEY, dateSectionOrder.value);
      sections.value = [...response.items].sort((left, right) => compare(left.id, right.id));
      regroupRows();
    }
  }

  function refreshDepartureSections() {
    if (hasDateVisibility.value) void reload();
    else void loadSections().catch(() => {});
  }

  // Состав выборки по диапазонам определяет сервер, в том числе когда
  // строка переезжает из скрытой группы в видимую или обратно.
  const hasDateVisibility = computed(() => dateVisibility.items.length > 0
    && !includeDeleted.value && !includeHidden.value && !hiddenOnly.value
    && !observationActive.value);

  async function refreshAfterDateChange() {
    if (hasDateVisibility.value) await reload();
    else await loadSections().catch(() => {});
  }

  async function addDateSection(date: string) {
    const recordUndo = history.capture();
    const section = await api.post<(typeof sections.value)[number] & { created: boolean }>('/candidates/sections', { date });
    await reload();
    if (section.created) recordUndo({
      label: `Строка даты «${date.split('-').reverse().join('.')}»`,
      undo: async () => { await api.patch(`/candidates/sections/${section.id}`, { deleted: true }); await reload(); },
      redo: async () => { await api.patch(`/candidates/sections/${section.id}`, { deleted: false }); await reload(); },
    });
    return section;
  }

  async function persistDateSection(id: string, date: string): Promise<void> {
    const response = await api.patch<{ ok: true; item: { id: string; date: string; label: string } }>(
      `/candidates/sections/${id}`, { date },
    );
    const updated = sections.value.map((section) => section.id === id
      ? { ...section, date: response.item.date, label: response.item.label }
      : section);
    const compare = dateGroupComparator(updated, rows.value, EMPTY_GROUP_KEY, dateSectionOrder.value);
    sections.value = updated.sort((left, right) => compare(left.id, right.id));
    regroupRows();
    await reload();
  }

  async function updateDateSection(id: string, date: string): Promise<void> {
    const previous = sections.value.find((section) => section.id === id);
    if (!previous || previous.date === date) return;
    const recordUndo = history.capture();
    await persistDateSection(id, date);
    recordUndo({
      label: `Дата «${previous.date.split('-').reverse().join('.')}» → «${date.split('-').reverse().join('.')}»`,
      undo: async () => { await persistDateSection(id, previous.date); },
      redo: async () => { await persistDateSection(id, date); },
    });
  }

  async function deleteDateSection(id: string): Promise<boolean> {
    const recordUndo = history.capture();
    const date = sections.value.find((section) => section.id === id)?.date;
    const result = await api.delete<{ removed: boolean }>(`/candidates/sections/${id}`);
    if (result.removed) recordUndo({
      label: date ? `Удаление даты «${date.split('-').reverse().join('.')}»` : 'Удаление строки даты',
      undo: async () => { await api.patch(`/candidates/sections/${id}`, { deleted: false }); await reload(); },
      redo: async () => { await api.delete(`/candidates/sections/${id}`); await reload(); },
    });
    await reload();
    return result.removed;
  }

  /** Черта принадлежит строке и остаётся под ней при сортировке и переносе. */
  async function setRowSeparator(id: string, separatorAfter: boolean, record = true): Promise<boolean> {
    if (observationActive.value) return false;
    const recordUndo = history.capture();
    try {
      const row = rowById(id) ?? await api.get<CandidateRow>(`/candidates/${id}`);
      const previous = row.separatorAfter ?? false;
      if (previous === separatorAfter) return true;

      const saved = await api.patch<{
        id: string; separatorAfter: boolean; version: number;
        updatedAt: string; updatedBy: string | null; updatedByName: string | null;
      }>(
        `/candidates/${id}/separator`,
        { separatorAfter, version: row.version, clientOpId: newOpId() },
      );
      const index = rowIndexById.get(id);
      if (index !== undefined) {
        const current = rows.value[index];
        if (current && current.version <= saved.version) {
          replaceAt(index, {
            ...current,
            separatorAfter: saved.separatorAfter,
            version: saved.version,
            updatedAt: saved.updatedAt,
            updatedBy: saved.updatedBy,
            updatedByName: saved.updatedByName,
          });
        } else if (current) {
          await reload();
        }
      }

      if (record) recordUndo({
        label: separatorAfter ? 'Добавление черты' : 'Удаление черты',
        undo: async () => {
          if (!await setRowSeparator(id, previous, false)) throw new Error('Не удалось отменить черту');
        },
        redo: async () => {
          if (!await setRowSeparator(id, separatorAfter, false)) throw new Error('Не удалось повторить черту');
        },
      });
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.isConflict) await reload().catch(() => {});
      toasts.error(
        'Не удалось изменить черту',
        error instanceof ApiError ? error.message : 'Проверьте соединение',
      );
      return false;
    }
  }

  /** Переносит черту между строками одним запросом, не меняя порядок кандидатов. */
  async function moveRowSeparator(sourceId: string, targetId: string, record = true): Promise<boolean> {
    if (observationActive.value || sourceId === targetId) return false;
    const recordUndo = history.capture();
    try {
      const [source, target] = await Promise.all([
        rowById(sourceId) ?? api.get<CandidateRow>(`/candidates/${sourceId}`),
        rowById(targetId) ?? api.get<CandidateRow>(`/candidates/${targetId}`),
      ]);
      if (!source.separatorAfter || target.separatorAfter) {
        toasts.warning('Черту не удалось перенести',
          !source.separatorAfter ? 'Исходная черта уже удалена' : 'Под выбранной строкой уже есть черта');
        return false;
      }

      const saved = await api.post<{
        source: SeparatorChange;
        target: SeparatorChange;
      }>(`/candidates/${sourceId}/separator/move`, {
        targetId,
        sourceVersion: source.version,
        targetVersion: target.version,
        clientOpId: newOpId(),
      });

      let needsReload = false;
      for (const change of [saved.source, saved.target]) {
        const index = rowIndexById.get(change.id);
        if (index === undefined) continue;
        const current = rows.value[index];
        if (!current) continue;
        if (current.version > change.version) {
          needsReload = true;
          continue;
        }
        replaceAt(index, { ...current, ...change });
      }
      if (needsReload) await reload();

      if (record) recordUndo({
        label: 'Перенос черты',
        undo: async () => {
          if (!await moveRowSeparator(targetId, sourceId, false)) throw new Error('Не удалось отменить перенос черты');
        },
        redo: async () => {
          if (!await moveRowSeparator(sourceId, targetId, false)) throw new Error('Не удалось повторить перенос черты');
        },
      });
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.isConflict) await reload().catch(() => {});
      toasts.error('Не удалось перенести черту',
        error instanceof ApiError ? error.message : 'Проверьте соединение');
      return false;
    }
  }

  interface SeparatorChange {
    id: string;
    separatorAfter: boolean;
    version: number;
    updatedAt: string;
    updatedBy: string | null;
    updatedByName: string | null;
  }

  const rows = shallowRef<CandidateRow[]>([]);
  const hiddenGaps = shallowRef<HiddenRowGap[]>([]);
  const rowIndexById = new Map<string, number>();

  const loading = ref(false);
  const refreshing = ref(false);
  const loadingMore = ref(false);
  const initialised = ref(false);
  const error = ref<string | null>(null);

  const nextCursor = ref<string | null>(null);
  /** An observed viewport is temporary and never saved as the user's layout. */
  const observationActive = ref(false);
  const observationCache = new Map<string, CandidateRow>();
  let observationRequest = 0;
  let observationEpoch = 0;
  let observationNeedsPersonalReload = false;
  const total = ref<number>(0);
  const hasMore = computed(() => nextCursor.value !== null);

  // ── Представление ───────────────────────────────────────
  const sort = ref<Sort[]>([{ field: 'rowNumber', dir: 'asc' }]);
  const filters = ref<Filter[]>([]);
  const search = ref('');
  const includeDeleted = ref(false);
  const includeHidden = ref(false);
  const hiddenOnly = ref(false);
  const travelGroupFilter = ref<string | null>(null);
  let beforeHiddenView: {
    filters: Filter[]; search: string; travelGroupFilter: string | null;
    includeDeleted: boolean; includeHidden: boolean;
  } | null = null;

  const visibleColumnKeys = ref<string[]>([...DEFAULT_VISIBLE_COLUMNS]);
  const columnWidths = ref<Record<string, number>>({});
  const density = ref<GridDensity>('normal');
  /** Отдельная полоса слева от колонок; в новом виде по умолчанию скрыта. */
  const showRowNumbers = ref(false);
  /**
   * Закрепление колонок — состояние, а не константа в описании колонок.
   *
   * Порядок массива задаёт порядок в закреплённой области: закрепление
   * всегда «слева и подряд». Дырок вида «Город закреплён, а ФИО левее — нет»
   * не бывает — такое состояние невозможно ни описать, ни отрисовать.
   */
  const DEFAULT_PINNED = GRID_COLUMNS.filter((c) => c.pinned === 'left').map((c) => c.key);
  const pinnedKeys = ref<string[]>([...DEFAULT_PINNED]);

  /**
   * Закрепление справа.
   *
   * Слева держат то, по чему строку узнают: номер, ФИО. Справа — то,
   * ради чего в неё смотрят: статус, дата выезда, документы. При сорока
   * колонках это разные потребности, и одной левой областью они
   * не закрываются — приходилось гонять таблицу горизонтально туда-обратно.
   */
  const pinnedRightKeys = ref<string[]>([]);

  /** Жёсткий предел из viewConfigSchema: больше восьми не бывает. */
  const MAX_PINNED = 8;


  /**
   * Поле группировки. null — без группировки.
   *
   * Раньше здесь был флаг «по дням», и группы считались сравнением даты
   * с предыдущей строкой — без всякой связи с сортировкой. При обычной
   * сортировке по номеру это давало тридцать «групп» по одной строке.
   * Группировка обязана задавать первичную сортировку, иначе она
   * математически не работает.
   */
  const groupBy = ref<string | null>(null);

  /** Свёрнутые группы. Состояние просмотра, в представление не сохраняется. */
  const collapsedGroups = ref<Set<string>>(new Set());

  /*
    Поля группировки лежат в @yana/shared: этот же список проверяет сервер
    в запросе перемещения строки. Две копии разошлись бы на первом же новом
    поле, и перетаскивание в новой группировке начало бы отвечать 400.
  */

  const groupByLabel = computed(
    () => GROUPABLE_FIELDS.find((f) => f.key === groupBy.value)?.label ?? null,
  );

  /**
   * Сортировка, которая реально уходит на сервер.
   *
   * Поле группировки всегда первое: без этого строки одной группы
   * разъезжаются по всей выборке. То, что пользователь выбрал руками,
   * становится сортировкой внутри группы.
   */
  const effectiveSort = computed<Sort[]>(() => {
    if (!groupBy.value) return sort.value;

    const groupField = groupBy.value;
    const existing = sort.value.find((s) => s.field === groupField);
    const rest = sort.value.filter((s) => s.field !== groupField);

    const dir = groupField === 'sectionId' ? dateSectionOrder.value : existing?.dir ?? (groupField.toLowerCase().includes('date') ? 'desc' : 'asc');
    return [{ field: groupField, dir }, ...rest].slice(0, 3);
  });

  async function setGroupBy(field: string | null): Promise<void> {
    groupBy.value = field;
    collapsedGroups.value = new Set();
    // Порядок групп до ответа сервера — по тому, что уже на экране:
    // иначе первая же правка ячейки переставила бы группы местами
    rememberGroupOrder();
    await reload();
  }

  function setDateSectionOrder(order: DateGroupOrder): void {
    if (dateSectionOrder.value === order) return;
    dateSectionOrder.value = order;
    const compare = dateGroupComparator(sections.value, rows.value, EMPTY_GROUP_KEY, order);
    sections.value = [...sections.value].sort((left, right) => compare(left.id, right.id));
    regroupRows();
    // Старый режим группировки по строке даты ещё может быть открыт у пользователя.
    // Его серверный курсор тоже должен перейти на новый порядок.
    if (groupBy.value === 'sectionId' && !observationActive.value) void reload();
  }

  function toggleGroupCollapsed(key: string, all = false): void {
    const next = new Set(collapsedGroups.value);
    if (all) {
      // Alt+клик: свернуть всё или развернуть всё, отталкиваясь от текущей
      if (next.has(key)) next.clear();
      else {
        for (const g of allGroupKeys.value) next.add(g);
      }
    } else if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    collapsedGroups.value = next;
  }

  const allGroupKeys = computed(() => {
    const seen = new Set<string>(sections.value.map((section) => section.id));
    for (const row of rows.value) {
      if (row.sectionId) seen.add(row.sectionId);
      else if (groupBy.value && groupBy.value !== 'sectionId') seen.add(groupKeyOf(row, groupBy.value));
    }
    return [...seen];
  });

  /**
   * Ключ группы «Не заполнено».
   *
   * Нулевой байт плюс слово: такого не бывает в значениях. Менять нельзя —
   * им же помечены свёрнутые группы, которые хранятся ключами.
   */
  const EMPTY_GROUP_KEY = '\u0000empty';

  /** Ключ группы строки — по нему строки склеиваются и сворачиваются. */
  function groupKeyOf(row: CandidateRow, field: string): string {
    const v = (row as unknown as Record<string, unknown>)[field];
    return v === null || v === undefined || v === '' ? EMPTY_GROUP_KEY : String(v);
  }

  /**
   * Раскладка таблицы переживает перезагрузку.
   *
   * Рекрутёр настраивает набор колонок и ширины под свою работу один раз;
   * если после F5 всё сбрасывается, настраивать перестают вовсе.
   * Хранится локально — это личная настройка рабочего места, а не данные.
   */
  const LAYOUT_KEY = 'yana.grid.layout.v1';
  /*
    Версия раскладки — для разовых поправок вроде колонки, которую
    сначала вернули, а потом забрали. Растёт только тогда, когда
    сохранённое у людей надо один раз тронуть.
  */
  const LAYOUT_VERSION = 6;

  /**
   * Колонки, появившиеся в схеме после того, как человек настроил таблицу.
   *
   * Сохранённая раскладка — это список видимых ключей, и новой колонки
   * в нём быть не может по определению. Без этой добавки «Проект», заведённый
   * вместе с импортом рабочей таблицы, не увидел бы никто из тех, кто хоть раз
   * открывал грид: колонка есть в схеме и в данных, но не в чьём-то localStorage.
   *
   * Отличить новую колонку от убранной руками по одному списку видимых нельзя,
   * поэтому рядом хранится `known` — все ключи схемы на момент сохранения.
   * Новая та, которой в нём не было; убранная в нём есть, и её не возвращаем.
   *
   * `known: undefined` — раскладка, сохранённая до появления этого поля.
   * Там различить нечем, и однократно показываем все колонки по умолчанию:
   * лишняя колонка, которую видно и можно убрать, лучше отсутствующей,
   * о которой человек не догадывается. Со следующим сохранением `known`
   * появится, и догадки закончатся.
   */
  function withNewColumns(saved: string[], known?: string[]): string[] {
    const wasKnown = new Set(known ?? []);
    const visible = new Set(saved);
    const result = [...saved];

    for (const [index, col] of GRID_COLUMNS.entries()) {
      if (visible.has(col.key) || !col.visibleByDefault) continue;
      if (known && wasKnown.has(col.key)) continue; // убрана сознательно

      // Ставим на её место по схеме: хвост списка человек не разглядывает
      const anchor = GRID_COLUMNS.slice(0, index)
        .map((c) => c.key)
        .filter((k) => visible.has(k))
        .pop();
      result.splice(anchor ? result.indexOf(anchor) + 1 : 0, 0, col.key);
      visible.add(col.key);
    }

    return result;
  }

  function loadLayout(): void {
    try {
      const raw = localStorage.getItem(LAYOUT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as Partial<{
        layoutVersion: number;
        columns: string[];
        known: string[];
        pinnedRight: string[];
        widths: Record<string, number>;
        density: GridDensity;
        showRowNumbers: boolean;
        dateSectionOrder: DateGroupOrder;
        groupBy: string | null;
        groupByAddedDate: boolean;
        pinned: string[];
      }>;

      // Колонки могли исчезнуть из схемы между релизами — фильтруем
      if (Array.isArray(saved.columns)) {
        const valid = saved.columns.filter(publicColumn);
        if (valid.length > 0) {
          /*
            Колонку «Едут вместе» разово возвращали — и разово убираем.

            В версии 2 раскладки её добавляли всем: думали, что иначе не
            видно, что группы вообще бывают. Оказалось, что видно —
            цветом, а колонка с названием повторяет тот же факт словами.
            Поэтому версия 3 забирает обратно ровно то, что добавила
            вторая: если человек включил колонку сам, у него уже стоит
            третья версия, и трогать нечего.
          */
          const addedByUs = saved.layoutVersion === 2;
          const cleaned = addedByUs ? valid.filter((k) => k !== 'travelGroupId') : valid;

          /*
            Четвёртая версия разово выстраивает колонки в порядок рабочей
            таблицы — решение заказчика: сначала двенадцать колонок листа
            «2026г», следом остальное.

            Без этой поправки новый порядок не увидел бы никто из тех, кто
            хоть раз открывал грид: сохранённая раскладка — это список
            ключей, и порядок в нём свой, прежний. Что показывать, а что
            прятать, человек решает сам, и это остаётся как было; трогаем
            только очерёдность.
          */
          const ordered =
            (saved.layoutVersion ?? 0) < 4
              ? [...cleaned].sort(
                  (a, b) =>
                    GRID_COLUMNS.findIndex((c) => c.key === a) -
                    GRID_COLUMNS.findIndex((c) => c.key === b),
                )
              : cleaned;

          visibleColumnKeys.value = withNewColumns(
            ordered,
            Array.isArray(saved.known) ? saved.known : undefined,
          );
          // «Объект» уже был в схеме, но оставался скрытым. Версия 5 один
          // раз показывает его перед старым текстом работы; порядок других
          // колонок, настроенный человеком, сохраняется.
          if ((saved.layoutVersion ?? 0) < 5) {
            const migrated = visibleColumnKeys.value.filter((key) => key !== 'workObjectId');
            const workNoteIndex = migrated.indexOf('workNote');
            const statusIndex = migrated.indexOf('statusId');
            const insertAt = workNoteIndex >= 0 ? workNoteIndex : statusIndex >= 0 ? statusIndex + 1 : migrated.length;
            migrated.splice(insertAt, 0, 'workObjectId');
            visibleColumnKeys.value = migrated;
          }
          // Версия 6 разово скрывает прежний свободный текст работы у всех.
          // Повторное ручное включение после обновления сохраняется как обычно.
          if ((saved.layoutVersion ?? 0) < 6) {
            visibleColumnKeys.value = visibleColumnKeys.value.filter((key) => key !== 'workNote');
          }
        }
      }
      if (saved.widths) columnWidths.value = saved.widths;
      if (saved.density) density.value = saved.density;
      if (typeof saved.showRowNumbers === 'boolean') showRowNumbers.value = saved.showRowNumbers;
      if (saved.dateSectionOrder === 'asc' || saved.dateSectionOrder === 'desc') dateSectionOrder.value = saved.dateSectionOrder;
      if (Array.isArray(saved.pinnedRight)) {
        pinnedRightKeys.value = saved.pinnedRight.filter(publicColumn);
      }
      /*
        Закрепление слева тоже разово возвращается к схемному.

        Закреплённая колонка физически переезжает в левый край, поэтому
        старое закрепление («№», «Ведёт», «ФИО») перебило бы порядок листа
        ровно там, где он важнее всего — в первых колонках. Схемное
        закрепление — «Проект», «№», «ФИО» — этот же порядок и повторяет.
      */
      if (Array.isArray(saved.pinned) && (saved.layoutVersion ?? 0) >= 4) {
        pinnedKeys.value = saved.pinned.filter(publicColumn);
      }

      if (typeof saved.groupBy === 'string' || saved.groupBy === null) {
        groupBy.value = VIEW_GROUPABLE_FIELDS.some((field) => field.key === saved.groupBy) ? saved.groupBy : null;
      }
      if ((saved.layoutVersion ?? 0) < LAYOUT_VERSION) saveLayout();
    } catch {
      // Битая настройка не должна мешать открыть таблицу
    }
  }

  function saveLayout(): void {
    if (observationActive.value) return;
    try {
      localStorage.setItem(
        LAYOUT_KEY,
        JSON.stringify({
          layoutVersion: LAYOUT_VERSION,
          columns: visibleColumnKeys.value,
          // Все ключи схемы на момент сохранения: по ним при следующей
          // загрузке видно, какие колонки появились с тех пор
          known: GRID_COLUMNS.map((c) => c.key),
          widths: columnWidths.value,
          density: density.value,
          showRowNumbers: showRowNumbers.value,
          dateSectionOrder: dateSectionOrder.value,
          groupBy: groupBy.value,
          pinned: pinnedKeys.value,
          pinnedRight: pinnedRightKeys.value,
        }),
      );
    } catch {
      // Приватный режим браузера — молча продолжаем без сохранения
    }
  }

  loadLayout();

  /**
   * Раскладка сохраняется с задержкой и от всех источников.
   *
   * `pinnedRightKeys` в списке не было — закрепление справа записывалось
   * только заодно с другой правкой и до перезагрузки не доживало.
   * А тягание границы колонки даёт полсотни событий в секунду, и каждое
   * писало в localStorage синхронно, останавливая отрисовку.
   */
  watch(
    [visibleColumnKeys, columnWidths, density, showRowNumbers, dateSectionOrder, groupBy, pinnedKeys, pinnedRightKeys],
    useDebounceFn(saveLayout, 300),
    { deep: true },
  );

  function resetLayout(): void {
    visibleColumnKeys.value = [...DEFAULT_VISIBLE_COLUMNS];
    columnWidths.value = {};
    density.value = 'normal';
    showRowNumbers.value = false;
    groupBy.value = null;
    setDateSectionOrder('asc');
    collapsedGroups.value = new Set();
    pinnedKeys.value = [...DEFAULT_PINNED];
    // Закрепление справа тоже часть раскладки: без этой строки «По умолчанию»
    // оставляло приклеенные справа колонки и выглядело как полусброс
    pinnedRightKeys.value = [];
  }

  /**
   * Высота строки и сколько строк текста в неё влезает.
   *
   * Числа фиксированные, одни на весь вид: виртуализатор считает смещения
   * умножением, и высота «по содержимому» означала бы замер каждой строки —
   * прокрутка начинает дёргаться, а прыжок к строке по номеру перестаёт
   * попадать. Ровно поэтому в Airtable высота строки тоже переключатель
   * на весь вид, а не автоподбор.
   */
  const ROW_HEIGHTS: Record<GridDensity, { height: number; lines: number }> = {
    compact: { height: 28, lines: 1 },
    normal: { height: 34, lines: 1 },
    comfortable: { height: 42, lines: 1 },
    lines2: { height: 56, lines: 2 },
    lines4: { height: 94, lines: 4 },
  };

  const rowHeight = computed(() => ROW_HEIGHTS[density.value].height);

  /** Сколько строк текста показывает ячейка. Больше одной — значит с переносом. */
  const cellLines = computed(() => ROW_HEIGHTS[density.value].lines);
  const wrapText = computed(() => cellLines.value > 1);

  const visibleColumns = computed<ColumnMeta[]>(() =>
    visibleColumnKeys.value
      .map((k) => COLUMN_BY_KEY[k])
      .filter((c): c is ColumnMeta => Boolean(c) && !c!.internal),
  );


  /**
   * Узкий экран. Не «мобильная версия», а ровно один вопрос: помещается ли
   * закреплённая область так, чтобы рядом осталось место для данных.
   */
  const isNarrowViewport = useMediaQuery('(max-width: 640px)');

  /**
   * Что реально закреплено сейчас.
   *
   * На телефоне закреплённая область в 280 пикселей — это весь экран:
   * «№», «Ведёт» и ФИО занимают всё, а прокручиваемым колонкам, ради которых
   * таблицу и открыли, остаётся полоска. Поэтому на узком экране остаётся
   * только ФИО: без имени строка не опознаётся, всё остальное — читается
   * прокруткой. Настройка человека при этом цела: `pinnedKeys` не трогаем,
   * на широком экране всё вернётся как было.
   */
  const effectivePinnedKeys = computed<string[]>(() => {
    if (!isNarrowViewport.value) return pinnedKeys.value;
    return pinnedKeys.value.filter((k) => k === 'fullName');
  });

  /** На телефоне правая область съедает последние пиксели — она там не нужна. */
  const effectivePinnedRightKeys = computed<string[]>(() =>
    isNarrowViewport.value ? [] : pinnedRightKeys.value,
  );

  const pinnedRightColumns = computed<ColumnMeta[]>(() =>
    effectivePinnedRightKeys.value
      .map((k) => COLUMN_BY_KEY[k])
      .filter((c): c is ColumnMeta => c !== undefined && !c.internal && visibleColumnKeys.value.includes(c.key)),
  );

  const pinnedColumns = computed<ColumnMeta[]>(() =>
    effectivePinnedKeys.value
      .map((k) => COLUMN_BY_KEY[k])
      .filter((c): c is ColumnMeta => c !== undefined && !c.internal && visibleColumnKeys.value.includes(c.key)),
  );

  const scrollColumns = computed(() =>
    visibleColumns.value.filter(
      (c) =>
        !effectivePinnedKeys.value.includes(c.key) &&
        !effectivePinnedRightKeys.value.includes(c.key),
    ),
  );

  const isPinned = (key: string) => pinnedKeys.value.includes(key);
  const isPinnedRight = (key: string) => pinnedRightKeys.value.includes(key);
  const canPinMore = computed(() => pinnedKeys.value.length < MAX_PINNED);

  function pinColumn(key: string): void {
    if (!publicColumn(key)) return;
    if (pinnedKeys.value.includes(key) || pinnedKeys.value.length >= MAX_PINNED) return;
    // В конец закреплённой области: колонка физически переезжает влево
    pinnedKeys.value = [...pinnedKeys.value, key];
  }

  /** Закрепить справа. Колонка уходит из прокручиваемой области к правому краю. */
  function pinRightColumn(key: string): void {
    if (!publicColumn(key)) return;
    if (pinnedRightKeys.value.includes(key) || pinnedRightKeys.value.length >= MAX_PINNED) return;
    // Слева и справа одновременно колонка быть не может
    pinnedKeys.value = pinnedKeys.value.filter((k) => k !== key);
    pinnedRightKeys.value = [...pinnedRightKeys.value, key];
  }

  function unpinColumn(key: string): void {
    pinnedKeys.value = pinnedKeys.value.filter((k) => k !== key);
    pinnedRightKeys.value = pinnedRightKeys.value.filter((k) => k !== key);
  }

  /**
   * Часть шапки, в которой стоит колонка. Порядок каждой части хранится
   * в своём массиве, и переставлять надо именно тот, где колонка лежит.
   */
  function areaOf(key: string): 'left' | 'scroll' | 'right' {
    if (pinnedKeys.value.includes(key)) return 'left';
    if (pinnedRightKeys.value.includes(key)) return 'right';
    return 'scroll';
  }

  /**
   * Перестановка колонки: `key` встаёт перед `beforeKey`, а `beforeKey: null`
   * отправляет её в конец своей части шапки.
   *
   * Двигать можно только внутри своей части. Перенос из прокручиваемой
   * области в закреплённую — это не порядок, а закрепление: у него есть
   * предел в восемь колонок, предупреждение про половину ширины
   * и подтверждение, и всё это живёт в меню заголовка. Жест, который обходил
   * бы эти проверки, — вторая дверь с другим замком, поэтому разные части
   * здесь просто ничего не делают.
   *
   * Перестановка в прокручиваемой области правит `visibleColumnKeys`, где
   * вперемешку лежат и закреплённые ключи. Это не мешает: положение
   * закреплённой колонки в этом массиве на отрисовку не влияет — порядок
   * закреплённой области берётся из `pinnedKeys`.
   */
  function moveColumn(key: string, beforeKey: string | null): void {
    if (key === beforeKey) return;
    const area = areaOf(key);
    if (beforeKey !== null && areaOf(beforeKey) !== area) return;

    const list =
      area === 'left' ? pinnedKeys : area === 'right' ? pinnedRightKeys : visibleColumnKeys;

    const next = list.value.filter((k) => k !== key);
    if (next.length === list.value.length) return; // колонки в списке нет

    const at = beforeKey === null ? next.length : next.indexOf(beforeKey);
    if (at < 0) return;
    next.splice(at, 0, key);
    list.value = next;
  }

  /** Ширина закреплённой области, если закрепить ещё одну колонку. */
  function pinnedWidthWith(key: string): number {
    const col = COLUMN_BY_KEY[key];
    return pinnedWidth.value + (col ? widthOf(col) : 0);
  }

  function widthOf(col: ColumnMeta): number {
    const saved = columnWidths.value[col.key] ?? col.width;
    // Предел важнее сохранённой раскладки: у тех, кто настраивал таблицу
    // раньше, «Ведёт» так и осталась бы шириной в полтораста пикселей
    // ради кружка с инициалами
    const capped = col.maxWidth ? Math.min(saved, col.maxWidth) : saved;
    return col.minWidth ? Math.max(capped, col.minWidth) : capped;
  }

  const pinnedWidth = computed(() =>
    pinnedColumns.value.reduce((sum, c) => sum + widthOf(c), 0),
  );

  const pinnedRightWidth = computed(() =>
    pinnedRightColumns.value.reduce((sum, c) => sum + widthOf(c), 0),
  );

  /**
   * Подсветка состояния ячеек НЕ хранится в сторе.
   *
   * Она живёт доли секунды, ни на что не влияет, и если держать её
   * в реактивном объекте, который читает каждая ячейка, то правка одного
   * поля перерисовывает всю таблицу — а это и было причиной мигания.
   * Подробности — в lib/cellDecorations.ts.
   */

  // ── Загрузка ────────────────────────────────────────────

  function reindex(): void {
    rowIndexById.clear();
    rows.value.forEach((r, i) => rowIndexById.set(r.id, i));
  }

  function buildQuery(cursor?: string | null) {
    return {
      limit: PAGE_SIZE,
      // «2026г» открывает всю выборку одним запросом. Страницы оставлены
      // только для совместимости служебной догрузки старого снимка.
      ...(!cursor ? { mode: 'all' as const } : {}),
      ...(cursor ? { cursor } : {}),
      // Не sort, а effectiveSort: при включённой группировке поле группы
      // обязано быть первым уровнем сортировки
      sort: effectiveSort.value,
      filters: filters.value,
      ...(search.value.trim() ? { search: search.value.trim() } : {}),
      includeDeleted: includeDeleted.value,
      includeHidden: includeHidden.value,
      hiddenOnly: hiddenOnly.value,
      ...(travelGroupFilter.value ? { travelGroupId: travelGroupFilter.value } : {}),
    };
  }

  interface Page {
    items: CandidateRow[];
    hiddenGaps?: HiddenRowGap[];
    nextCursor: string | null;
    /**
     * `null` — «не считали», а не «ноль». Сервер считает счётчик только
     * для первой страницы: от курсора он не зависит, а пересчёт проходит
     * выборку заново, до ста тысяч строк на каждую догрузку.
     */
    totalEstimate: number | null;
  }

  /**
   * Номер поколения выборки.
   *
   * Запросы списка запускаются от каждого движения человека: поиск по букве,
   * переключение фильтра, смена сортировки, возврат на вкладку. Идут они
   * параллельно и приходят в произвольном порядке — тяжёлый запрос с поиском
   * по всей базе легко обгоняется следующим, более узким. Раньше побеждал
   * тот, кто пришёл последним, а не тот, кого ждали: ответ отставшего
   * затирал `rows`, `nextCursor` и `total` целиком, и в таблице оказывалась
   * выборка по фильтру, который пользователь уже снял. Курсор при этом
   * тоже был чужой — догрузка продолжала не ту выборку.
   */
  let requestSeq = 0;
  let loadMoreRequest: Promise<void> | null = null;
  let rowsTruncated = false;
  let loadedQuery: string | null = null;
  let entryRequest: { query: string; seq: number; promise: Promise<void> } | null = null;

  function hasLoadedRows(): boolean {
    return initialised.value && !error.value && !observationActive.value
      && loadedQuery === JSON.stringify(buildQuery());
  }

  /** Возврат показывает имеющиеся строки; запрос только освежает их в фоне. */
  function enter(): Promise<void> {
    const query = JSON.stringify(buildQuery());
    if (entryRequest?.query === query && entryRequest.seq === requestSeq) return entryRequest.promise;
    const request = load({ background: hasLoadedRows() });
    entryRequest = { query, seq: requestSeq, promise: request };
    void request.finally(() => { if (entryRequest?.promise === request) entryRequest = null; });
    return request;
  }

  async function load(options: { background?: boolean } = {}): Promise<void> {
    if (observationActive.value) return;
    const seq = ++requestSeq;
    const query = buildQuery();
    const queryKey = JSON.stringify(query);
    const background = !!options.background && hasLoadedRows();
    loading.value = !background;
    refreshing.value = true;
    error.value = null;
    try {
      if (background) await waitForSaves();
      if (seq !== requestSeq) return;
      const before = new Map(rows.value.map(row => [row.id, row]));
      const [page] = await Promise.all([
        api.post<Page>('/candidates/list', query),
        loadSections(),
        dateVisibility.load(true),
      ]);
      // Пока ждали ответ, запустили ещё одну выборку — этот результат
      // уже неактуален. Молча выходим, не трогая ни строк, ни курсора
      if (seq !== requestSeq) return;

      const current = new Map(rows.value.map(row => [row.id, row]));
      const membershipChanged = current.size !== before.size || [...current.keys()].some(id => !before.has(id));
      // Правка или изменение состава во время GET требуют актуального снимка.
      if (background && (saveQueue.size || membershipChanged)) {
        await waitForSaves();
        if (seq === requestSeq) await load({ background: true });
        return;
      }
      const incoming = background ? page.items.map(row => {
        const local = current.get(row.id);
        return local && local !== before.get(row.id) && local.version >= row.version ? local : row;
      }) : page.items;
      rows.value = reconcileRows(rows.value, orderSectionRows(incoming));
      hiddenGaps.value = page.hiddenGaps ?? [];
      rowsTruncated = false;
      nextCursor.value = page.nextCursor;
      total.value = page.totalEstimate ?? 0;
      reindex();
      rememberGroupOrder();
      initialised.value = true;
      loadedQuery = queryKey;
    } catch (e) {
      if (seq !== requestSeq) return;
      const message = e instanceof ApiError ? e.message : 'Не удалось загрузить данные';
      if (e instanceof ApiError && [401, 403, 404].includes(e.status)) {
        discardObservation();
        error.value = message;
      } else if (background) toasts.error('Не удалось обновить таблицу', message);
      else error.value = message;
    } finally {
      // Флаг снимает только последний запрос: иначе отставший гасит
      // индикатор загрузки, которая ещё идёт
      if (seq === requestSeq) {
        loading.value = false;
        refreshing.value = false;
      }
    }
  }

  function loadMore(): Promise<void> {
    if (loadMoreRequest) return loadMoreRequest;
    loadMoreRequest = loadMorePage().finally(() => { loadMoreRequest = null; });
    return loadMoreRequest;
  }

  async function loadMorePage(): Promise<void> {
    if (observationActive.value) return;
    if (loading.value || !nextCursor.value) return;
    // То же поколение, что у load(): страница по устаревшему курсору
    // не должна дописываться в уже сменившуюся выборку
    const seq = requestSeq;
    const query = buildQuery(nextCursor.value);
    const view = JSON.stringify(buildQuery());
    loadingMore.value = true;
    error.value = null;
    try {
      const page = await api.post<Page>('/candidates/list', query);
      if (seq !== requestSeq || view !== JSON.stringify(buildQuery()) || observationActive.value) return;

      // Курсорная пагинация не даёт дублей, но подстраховаться дешевле,
      // чем ловить «одна и та же строка дважды» в отчёте пользователя
      const known = rowIndexById;
      const fresh = page.items.filter((r) => !known.has(r.id));

      const previousRows = rows.value;
      const combined = rows.value.concat(fresh);
      if (!groupBy.value && sort.value.length === 1 && sort.value[0]?.field === 'rowNumber' && sort.value[0]?.dir === 'asc') {
        combined.sort((left, right) => left.rowNumber - right.rowNumber);
      }
      rows.value = orderSectionRows(combined);
      reindex();
      const pairs = previousRows.map((row, index) => [index, rowIndexById.get(row.id)!] as [number, number]);
      if (pairs.some(([from, to]) => from !== to)) rowRemap.value = { pairs, seq: ++shiftSeq };
      // Страница могла принести группы, которых в выборке ещё не было
      rememberGroupOrder();

      nextCursor.value = page.nextCursor;
      // Догрузка счётчик не приносит — оставляем тот, что посчитала
      // первая страница, иначе число в шапке обнулится на первой же прокрутке
      if (page.totalEstimate !== null) total.value = page.totalEstimate;

      if (rows.value.length >= MAX_ROWS_IN_MEMORY && nextCursor.value) {
        // Держать в памяти всю стотысячную таблицу незачем: пользователь
        // всё равно не пролистает её глазами, а вкладка съест гигабайт
        toasts.info(
          'Показаны не все строки',
          'Загружено 20 000 строк. Уточните фильтр или поиск, чтобы найти нужное.',
        );
        rowsTruncated = true;
        nextCursor.value = null;
      }

    } catch (e) {
      if (seq !== requestSeq || view !== JSON.stringify(buildQuery())) return;
      error.value = e instanceof ApiError ? e.message : 'Не удалось догрузить строки';
    } finally {
      loadingMore.value = false;
    }
  }

  /** Догрузка для выделения всей выборки, независимо от лимита массовой правки. */
  async function loadAllRows(shouldContinue: () => boolean = () => true): Promise<boolean> {
    const seq = requestSeq;
    const view = JSON.stringify(buildQuery());
    const current = () => shouldContinue() && !observationActive.value && !loading.value
      && seq === requestSeq && view === JSON.stringify(buildQuery());
    while (current() && nextCursor.value) {
      const cursor = nextCursor.value;
      const count = rows.value.length;
      await loadMore();
      // Ошибка или страница без продвижения не должны запускать бесконечный цикл.
      if (!current() || error.value || nextCursor.value === cursor
        || (nextCursor.value && rows.value.length === count)) return false;
    }
    return current() && !nextCursor.value && !rowsTruncated;
  }

  /** Смена сортировки, фильтров или поиска всегда начинает выборку заново. */
  async function reload(): Promise<void> {
    if (observationActive.value) {
      // Realtime resync must refresh the viewport, not replace it with page one.
      // Личный снимок при выходе тоже станет устаревшим, в том числе если
      // коллега скрыл строку вне наблюдаемого окна.
      observationNeedsPersonalReload = true;
      await applyObservationWindow(rows.value.map((row) => row.id), true);
      return;
    }
    nextCursor.value = null;
    await load();
  }

  function observationView(): Record<string, unknown> {
    return JSON.parse(JSON.stringify({
      columns: visibleColumnKeys.value, widths: columnWidths.value,
      pinned: pinnedKeys.value, pinnedRight: pinnedRightKeys.value,
      density: density.value, groupBy: groupBy.value,
      collapsedGroups: [...collapsedGroups.value],
      sort: sort.value, filters: filters.value, search: search.value,
      includeDeleted: includeDeleted.value, travelGroupId: travelGroupFilter.value,
      total: total.value,
    })) as Record<string, unknown>;
  }

  function applyObservationView(view: Record<string, unknown>): void {
    const config = viewConfigSchema.parse(view);
    visibleColumnKeys.value = config.columns.filter(publicColumn);
    columnWidths.value = config.widths;
    pinnedKeys.value = config.pinned.filter(publicColumn);
    pinnedRightKeys.value = config.pinnedRight.filter((key) => publicColumn(key) && !pinnedKeys.value.includes(key));
    density.value = config.density;
    groupBy.value = VIEW_GROUPABLE_FIELDS.some((field) => field.key === config.groupBy) ? config.groupBy : null;
    collapsedGroups.value = new Set(Array.isArray(view.collapsedGroups)
      ? view.collapsedGroups.filter((key): key is string => typeof key === 'string').slice(0, 1000) : []);
    sort.value = config.sort;
    filters.value = config.filters;
    search.value = config.search ?? '';
    includeDeleted.value = config.includeDeleted;
    travelGroupFilter.value = config.travelGroupId;
    if (typeof view.total === 'number' && Number.isFinite(view.total)) total.value = Math.max(0, view.total);
  }

  function beginObservation() {
    const snapshot = {
      epoch: observationEpoch,
      view: observationView(), rows: rows.value, hiddenGaps: hiddenGaps.value, nextCursor: nextCursor.value,
      total: total.value, initialised: initialised.value, error: error.value,
    };
    observationActive.value = true;
    refreshing.value = false;
    hiddenGaps.value = [];
    observationNeedsPersonalReload = false;
    // Discard ordinary requests that were already in flight when follow started.
    requestSeq += 1;
    observationRequest += 1;
    observationCache.clear();
    for (const row of rows.value) observationCache.set(row.id, row);
    loading.value = false;
    loadingMore.value = false;
    nextCursor.value = null;
    return snapshot;
  }

  async function applyObservationWindow(ids: string[], refresh = false): Promise<void> {
    if (!observationActive.value) return;
    const request = ++observationRequest;
    const wanted = [...new Set(ids)].slice(0, 300);
    // Keep locally received cell updates when the same viewport is reported again.
    if (refresh) observationCache.clear();
    else for (const row of rows.value) observationCache.set(row.id, row);
    const missing = wanted.filter((id) => !observationCache.has(id));
    if (missing.length) {
      const response = await api.post<{ items: CandidateRow[] }>('/candidates/observation-window', { ids: missing });
      if (!observationActive.value || request !== observationRequest) return;
      for (const row of response.items) observationCache.set(row.id, row);
    }
    if (!observationActive.value || request !== observationRequest) return;
    rows.value = wanted.flatMap((id) => observationCache.has(id) ? [observationCache.get(id)!] : []);
    reindex();
    rememberGroupOrder();
    initialised.value = true;
    error.value = null;
    // A bounded cache prevents a long observation session from accumulating the table.
    if (observationCache.size > 600) {
      observationCache.clear();
      for (const row of rows.value) observationCache.set(row.id, row);
    }
  }

  async function endObservation(snapshot: ReturnType<typeof beginObservation>): Promise<boolean> {
    if (snapshot.epoch !== observationEpoch) return false;
    const needsReload = observationNeedsPersonalReload;
    observationNeedsPersonalReload = false;
    observationRequest += 1;
    for (const row of rows.value) observationCache.set(row.id, row);
    applyObservationView(snapshot.view);
    rows.value = needsReload ? [] : snapshot.rows.map((row) => {
      const latest = observationCache.get(row.id);
      return latest && latest.version >= row.version ? latest : row;
    });
    hiddenGaps.value = needsReload ? [] : snapshot.hiddenGaps;
    nextCursor.value = needsReload ? null : snapshot.nextCursor;
    total.value = snapshot.total;
    initialised.value = needsReload ? false : snapshot.initialised;
    error.value = snapshot.error;
    reindex();
    rememberGroupOrder();
    observationCache.clear();
    observationActive.value = false;
    // Пока запрос идёт, старые строки не показываем: среди них может быть
    // скрытая коллегой строка. Выборка и счётчик придут тем же фильтром.
    if (needsReload) await load();
    return true;
  }

  /** A different login must never inherit rows or pending reads from this session. */
  function discardObservation(): void {
    loadedQuery = null;
    entryRequest = null;
    refreshing.value = false;
    observationEpoch += 1;
    observationRequest += 1;
    requestSeq += 1;
    sectionRequest += 1;
    observationCache.clear();
    observationNeedsPersonalReload = false;
    rows.value = [];
    hiddenGaps.value = [];
    sections.value = [];
    dateVisibility.reset();
    nextCursor.value = null;
    total.value = 0;
    initialised.value = false;
    loading.value = false;
    loadingMore.value = false;
    error.value = null;
    sort.value = [{ field: 'rowNumber', dir: 'asc' }];
    filters.value = [];
    search.value = '';
    includeDeleted.value = false;
    includeHidden.value = false;
    hiddenOnly.value = false;
    beforeHiddenView = null;
    travelGroupFilter.value = null;
    // Restore only persisted layout; neither a remote layout nor filter values survive.
    resetLayout();
    loadLayout();
    reindex();
    rememberGroupOrder();
    observationActive.value = false;
  }

  watch([() => auth.user?.id, () => auth.user?.role],
    () => discardObservation(), { flush: 'sync' });

  function setSort(field: string, additive = false): void {
    const existing = sort.value.find((s) => s.field === field);
    if (!additive) {
      sort.value = existing
        ? [{ field, dir: existing.dir === 'asc' ? 'desc' : 'asc' }]
        : [{ field, dir: 'asc' }];
    } else if (existing) {
      existing.dir = existing.dir === 'asc' ? 'desc' : 'asc';
      sort.value = [...sort.value];
    } else if (sort.value.length < 3) {
      sort.value = [...sort.value, { field, dir: 'asc' }];
    }
    void reload();
  }

  function setFilter(field: string, filter: Filter | null): void {
    filters.value = filters.value.filter((f) => f.field !== field);
    if (filter) filters.value = [...filters.value, filter];
    void reload();
  }

  function clearFilters(): void {
    filters.value = [];
    search.value = '';
    travelGroupFilter.value = null;
    // Корзина — тоже фильтр: без этого «Сбросить всё» оставляло в таблице
    // мягко удалённых, а панель об этом молчала
    includeDeleted.value = false;
    includeHidden.value = false;
    hiddenOnly.value = false;
    beforeHiddenView = null;
    void reload();
  }

  /** Режим восстановления не зависит от поиска и фильтров рабочего вида. */
  async function showHiddenRows(): Promise<void> {
    if (hiddenOnly.value) return;
    beforeHiddenView = {
      filters: [...filters.value], search: search.value, travelGroupFilter: travelGroupFilter.value,
      includeDeleted: includeDeleted.value, includeHidden: includeHidden.value,
    };
    filters.value = [];
    search.value = '';
    travelGroupFilter.value = null;
    includeDeleted.value = false;
    includeHidden.value = true;
    hiddenOnly.value = true;
    await reload();
  }

  async function leaveHiddenRows(): Promise<void> {
    if (!hiddenOnly.value) return;
    hiddenOnly.value = false;
    filters.value = beforeHiddenView?.filters ?? [];
    search.value = beforeHiddenView?.search ?? '';
    travelGroupFilter.value = beforeHiddenView?.travelGroupFilter ?? null;
    includeDeleted.value = beforeHiddenView?.includeDeleted ?? false;
    includeHidden.value = beforeHiddenView?.includeHidden ?? false;
    beforeHiddenView = null;
    await reload();
  }

  async function setRowsHidden(
    ids: string[], hidden: boolean, scope: 'personal' | 'all' = 'all', record = true,
  ): Promise<boolean> {
    if (!ids.length || observationActive.value) return false;
    const recordUndo = history.capture();
    try {
      const result = await api.post<{ ids: string[] }>('/candidates/visibility', {
        ids, hidden, scope, clientOpId: newOpId(),
      });
      await reload();
      // Сервер пропускает строки с уже нужной видимостью. Если отменять
      // всё выделение, такие строки изменятся, хотя исходная команда их не трогала.
      const changedIds = result.ids ?? [];
      if (record && changedIds.length > 0) recordUndo({
        label: `${hidden ? 'Скрытие' : 'Показ'} строк ${scope === 'personal' ? 'для себя' : 'для всех'} (${changedIds.length})`,
        undo: async () => {
          if (!await setRowsHidden(changedIds, !hidden, scope, false)) throw new Error('Не удалось отменить скрытие строк');
        },
        redo: async () => {
          if (!await setRowsHidden(changedIds, hidden, scope, false)) throw new Error('Не удалось повторить скрытие строк');
        },
      });
      return true;
    } catch (error) {
      toasts.error(
        hidden ? 'Не удалось скрыть строки' : 'Не удалось вернуть строки',
        error instanceof ApiError ? error.message : 'Проверьте соединение',
      );
      return false;
    }
  }

  /** Раскрывает отмеченный в таблице промежуток, учитывая оба вида скрытия. */
  async function revealHiddenGap(gap: HiddenRowGap): Promise<boolean> {
    const personalIds = gap.personalIds;
    const globalIds = auth.can('candidate.bulk_edit') ? gap.globalIds : [];
    if (personalIds.length === 0 && globalIds.length === 0) {
      toasts.info('Нет прав на восстановление', 'Строки скрыты для всех. Попросите редактора показать их.');
      return false;
    }

    // Один маркер — одно действие Undo, даже когда среди строк есть оба
    // вида скрытия или серверный предел в 1000 id делит блок на пачки.
    const recordUndo = history.capture();
    const changedPersonal: string[] = [];
    const changedGlobal: string[] = [];
    async function apply(ids: string[], hidden: boolean, scope: 'personal' | 'all', changed?: string[]) {
      for (let offset = 0; offset < ids.length; offset += 1000) {
        const result = await api.post<{ ids: string[] }>('/candidates/visibility', {
          ids: ids.slice(offset, offset + 1000), hidden, scope, clientOpId: newOpId(),
        });
        changed?.push(...(result.ids ?? []));
      }
    }
    async function replay(hidden: boolean) {
      try {
        await apply(changedPersonal, hidden, 'personal');
        await apply(changedGlobal, hidden, 'all');
      } finally {
        await reload();
      }
    }

    let complete = false;
    try {
      await apply(personalIds, false, 'personal', changedPersonal);
      await apply(globalIds, false, 'all', changedGlobal);
      complete = true;
    } catch (error) {
      toasts.error('Не удалось раскрыть все строки', error instanceof ApiError ? error.message : 'Проверьте соединение');
    } finally {
      await reload();
    }

    if (changedPersonal.length || changedGlobal.length) recordUndo({
      label: `Показ скрытых строк (${gap.hiddenCount})`,
      undo: () => replay(true),
      redo: () => replay(false),
    });
    if (complete) {
      if (gap.globalIds.length > globalIds.length) {
        toasts.info('Часть строк всё ещё скрыта', 'Строки, скрытые для всех, может показать редактор.');
      } else {
        toasts.success('Строки показаны', 'Скрытый промежуток раскрыт в таблице.');
      }
    }
    return complete;
  }

  // ── Правка ячейки ───────────────────────────────────────

  /**
   * Оптимистичная запись: значение появляется в гриде сразу, запрос уходит
   * следом. Если сервер отверг — откатываем и объясняем, что произошло.
   *
   * Возвращает предыдущее значение, чтобы стек отмены мог построить
   * компенсирующую операцию.
   */
  /**
   * Замена одной строки.
   *
   * Раньше строка правилась на месте, а перерисовку запускал triggerRef.
   * Через границу стора это не работало: хранилище обновлялось, а таблица
   * показывала старое значение — так пропадали и чужие правки, и результат
   * Ctrl+Z. Проверено: в хранилище версия 11 с новым текстом, в таблице —
   * предыдущий. Новая ссылка на массив надёжна при любом устройстве стора,
   * а копирование двухсот указателей на правку ячейки ничего не стоит.
   */
  /**
   * Строки в списке сдвинулись — начиная с `from` на `delta` мест.
   *
   * Выделение в таблице живёт номерами строк, а не их id: прямоугольник
   * пересчитывается на каждое движение мыши, и по номерам это на порядок
   * дешевле. Плата — любая вставка в середину списка уводит выделение
   * на соседнюю строку, а по нему работают удаление и массовая правка.
   *
   * Поэтому сдвиг объявляется здесь, а таблица правит по нему своё
   * выделение. Один канал на все локальные вставки: их три, и каждая
   * молча ломала бы номера по-своему.
   */
  const rowShift = ref<{ from: number; delta: number; seq: number } | null>(null);
  /** Строки поменялись местами: «был номер — стал номер». Считает тот же счётчик. */
  const rowRemap = ref<{ pairs: Array<[number, number]>; seq: number } | null>(null);
  let shiftSeq = 0;

  function noteShift(from: number, delta: number): void {
    rowShift.value = { from, delta, seq: ++shiftSeq };
  }

  function replaceAt(index: number, row: CandidateRow): void {
    const previous = rows.value[index];
    const next = rows.value.slice();
    next[index] = row;
    rows.value = next;

    // Строка сменила группу — значит и место в списке у неё теперь другое
    const field = groupBy.value;
    if (previous && (previous.sectionId !== row.sectionId ||
      (field && groupKeyOf(previous, field) !== groupKeyOf(row, field)))) {
      regroupRows();
    }
  }

  /**
   * Пересборка списка по группам.
   *
   * Правка поля, по которому группируют, — обычное дело: у строки меняют
   * статус, а таблица разложена по статусам. Значение при этом меняется
   * на месте, и строка остаётся лежать среди чужой группы.
   *
   * Показывать её там нельзя (заголовок группы появится второй раз,
   * с неверными цифрами), а показывать в новой группе, не двигая
   * в списке, — тоже: выделение живёт номерами строк, и рамка осталась бы
   * на прежнем номере, где теперь соседка. По ней работают удаление
   * и массовая правка.
   *
   * Поэтому строка переезжает по-настоящему, а таблице отдаётся карта
   * «был номер — стал номер», по которой она переносит выделение следом.
   * Порядок групп — по первой строке каждой, тот же, что на экране.
   */
  /**
   * Порядок групп, каким его задала сортировка на сервере.
   *
   * Нужен пересборке: она раскладывает строки по группам, и без опоры
   * на прежний порядок группы менялись бы местами от правки одной ячейки.
   * Обновляется там же, где приходят строки, — при загрузке и догрузке.
   */
  let groupOrder: string[] = [];

  function orderSectionRows(list: CandidateRow[]): CandidateRow[] {
    if (groupBy.value !== 'sectionId') {
      const ordinary = list.filter((row) => !row.sectionId);
      const dated = list.filter((row) => row.sectionId);
      const compare = dateGroupComparator(sections.value, list, EMPTY_GROUP_KEY, dateSectionOrder.value);
      dated.sort((left, right) => compare(left.sectionId!, right.sectionId!));
      return [...ordinary, ...dated];
    }
    const compare = dateGroupComparator(sections.value, list, EMPTY_GROUP_KEY, dateSectionOrder.value);
    return [...list].sort((left, right) => compare(groupKeyOf(left, 'sectionId'), groupKeyOf(right, 'sectionId')));
  }

  function rememberGroupOrder(): void {
    const field = groupBy.value;
    groupOrder = field ? [...new Set(rows.value.map((r) => groupKeyOf(r, field)))] : [];
    if (field === 'sectionId') groupOrder.sort(dateGroupComparator(sections.value, rows.value, EMPTY_GROUP_KEY, dateSectionOrder.value));
  }

  function regroupRows(): void {
    const field = groupBy.value;
    if (!field) {
      const next = orderSectionRows(rows.value);
      if (next.some((row, index) => row !== rows.value[index])) {
        const wasAt = new Map(rows.value.map((row, index) => [row.id, index]));
        rows.value = next;
        reindex();
        rowRemap.value = { pairs: next.map((row, index) => [wasAt.get(row.id)!, index]), seq: ++shiftSeq };
      }
      return;
    }

    const buckets = new Map<string, CandidateRow[]>();
    for (const row of rows.value) {
      const key = groupKeyOf(row, field);
      const bucket = buckets.get(key);
      if (bucket) bucket.push(row);
      else buckets.set(key, [row]);
    }

    /*
      Группы остаются на своих местах.

      Порядок берётся из последней загрузки, а не из текущего списка:
      иначе смена статуса у первой строки поднимала бы её новую группу
      наверх, и экран переставлялся бы целиком от правки одной ячейки.
      Группа, которой в выборке ещё не было, встаёт в конец.
    */
    const rank = new Map(groupOrder.map((key, i) => [key, i]));
    const compare = field === 'sectionId'
      ? dateGroupComparator(sections.value, rows.value, EMPTY_GROUP_KEY, dateSectionOrder.value)
      : (a: string, b: string) => (rank.get(a) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b) ?? Number.MAX_SAFE_INTEGER);
    const grouped = [...buckets.keys()]
      .sort(compare)
      .flatMap((key) => buckets.get(key)!);
    const next = orderSectionRows(grouped);
    // Список уже разложен по группам — трогать его незачем: лишняя ссылка
    // на массив перерисовала бы таблицу на каждой правке любой ячейки
    if (next.every((row, i) => rows.value[i] === row)) return;

    const wasAt = new Map<string, number>();
    rows.value.forEach((row, i) => wasAt.set(row.id, i));

    rows.value = next;
    reindex();

    const pairs: Array<[number, number]> = [];
    next.forEach((row, i) => {
      const was = wasAt.get(row.id);
      if (was !== undefined && was !== i) pairs.push([was, i]);
    });
    if (pairs.length > 0) rowRemap.value = { pairs, seq: ++shiftSeq };
  }

  // ── Перетаскивание строк ────────────────────────────────

  /**
   * Значения подписей, которые едут вместе с полем группировки.
   *
   * Строка переехала в чужую группу — значит у неё сменился не только
   * uuid статуса, но и его название с цветом. Взять их можно у якоря:
   * он в целевой группе и уже всё это знает. Без этого строка до ответа
   * сервера показывала бы старое название на новом месте, а на группировке
   * по статусу — ещё и старый цвет заливки (Р-15).
   */
  const GROUP_LABEL_FIELDS: Record<string, readonly string[]> = {
    statusId: ['statusName', 'statusColor', 'statusTextColor'],
    cityId: ['cityName', 'cityColor'],
    responsibleUserId: ['responsibleName', 'responsibleColor'],
    travelGroupId: ['travelGroupName', 'travelGroupColor'],
    addedDate: [],
    plannedDepartureDate: [],
  };

  /**
   * Перетаскивание доступно, только когда порядок задаёт номер строки.
   *
   * При сортировке по ФИО оно ничего бы не значило: строка встала бы
   * на место и отскочила обратно после перезагрузки. При номере по убыванию
   * «выше» и «ниже» меняются местами, и бросок уехал бы в противоположную
   * сторону. В корзине его нет по третьей причине — там номера
   * не переставляются вовсе.
   */
  const manualOrderAvailable = computed(
    () =>
      !includeDeleted.value &&
      sort.value.length === 1 &&
      sort.value[0]!.field === 'rowNumber' &&
      sort.value[0]!.dir === 'asc',
  );

  /**
   * Где строка стоит сейчас — в терминах запроса перемещения.
   *
   * Отсюда берётся отмена: вернуть строку значит перетащить её обратно
   * к тому же соседу. Компенсирующая операция, а не локальный откат, —
   * иначе Ctrl+Z затирал бы чужие правки, сделанные после нашей.
   */
  function placeOf(
    rowId: string,
    field = groupBy.value,
  ): RowPlace | null {
    // Ищем перебором, а не по rowIndexById: карту строит reindex(), и всякий,
    // кто положил строки в стор мимо него, получил бы молчаливое «строки
    // нет». Перебор двадцати тысяч указателей на один жест мышью ничего
    // не стоит.
    const idx = rows.value.findIndex((r) => r.id === rowId);
    if (idx < 0) return null;

    const key = field ? groupKeyOf(rows.value[idx]!, field) : null;
    const sameGroup = (r: CandidateRow) => !field || groupKeyOf(r, field) === key;
    const groupKey = key === null || key === EMPTY_GROUP_KEY ? null : key;

    for (let i = idx - 1; i >= 0; i--) {
      if (sameGroup(rows.value[i]!)) {
        return { anchorId: rows.value[i]!.id, position: 'after', groupKey };
      }
    }
    for (let i = idx + 1; i < rows.value.length; i++) {
      if (sameGroup(rows.value[i]!)) {
        return { anchorId: rows.value[i]!.id, position: 'before', groupKey };
      }
    }
    return { anchorId: null, position: 'before', groupKey };
  }

  /**
   * Перестановка в памяти — до ответа сервера.
   *
   * Строка переезжает по-настоящему: обновить у неё rowNumber на месте
   * недостаточно, этот массив никто не пересортировывает. Следом уходит
   * карта «был номер — стал номер»: выделение живёт номерами строк.
   */
  function moveRowLocally(
    movedId: string,
    anchorId: string | null,
    position: 'before' | 'after',
    groupKey: string | null,
    field = groupBy.value,
    departure?: DepartureGroupState,
  ): void {
    if (movedId === anchorId) return;

    const from = rows.value.findIndex((r) => r.id === movedId);
    if (from < 0) return;

    const moved = rows.value[from]!;
    const rest = rows.value.filter((r) => r.id !== movedId);
    const targetKey = groupKey ?? EMPTY_GROUP_KEY;

    // Поле группировки и его подписи берём у якоря: он уже в целевой группе
    let placed = moved;
    if (field && groupKeyOf(moved, field) !== targetKey) {
      const anchor = anchorId
        ? rest.find((r) => r.id === anchorId)
        : rest.find((r) => groupKeyOf(r, field) === targetKey);
      const patch: Record<string, unknown> = { [field]: groupKey };
      if (field === 'sectionId') {
        const section = sections.value.find((item) => item.id === groupKey);
        patch.sectionLabel = groupKey ? (section?.label ?? anchor?.sectionLabel ?? null) : null;
      }
      /*
        Подписи копируем ТОЛЬКО когда есть у кого. Раньше при отсутствии
        якоря они затирались в null, и строка на секунду теряла название
        статуса вместе с его цветом — а на пути отката чинить это уже
        некому. Прежние подписи неверны ровно до ответа сервера, который
        придёт следом и проставит настоящие.
      */
      if (anchor) {
        for (const label of GROUP_LABEL_FIELDS[field] ?? []) {
          patch[label] = (anchor as unknown as Record<string, unknown>)[label];
        }
      }
      placed = { ...moved, ...patch } as CandidateRow;
    }
    if (departure && field === 'sectionId') {
      placed = { ...placed, sectionId: departure.sectionId };
    }

    let at: number;
    if (anchorId === null) {
      // Бросок на заголовок группы: край группы среди уже переставленных
      const inGroup = rest
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => !field || groupKeyOf(r, field) === targetKey);
      if (inGroup.length === 0) {
        /*
          Других строк этой группы в окне нет — например, тащат
          единственную строку группы на её же заголовок. Куда её ставить,
          локально не известно: место группы в списке задавала как раз эта
          строка. Раньше здесь стояло `0` или `rest.length`, и строка
          улетала в начало (или конец) ВСЕЙ таблицы, утаскивая за собой
          заголовок своей группы — `groupRowsByKey` строит порядок групп
          по первой встреченной строке. Оставляем её на месте: порядок
          проставит ответ сервера.
        */
        at = Math.min(from, rest.length);
      } else {
        at = position === 'before' ? inGroup[0]!.i : inGroup[inGroup.length - 1]!.i + 1;
      }
    } else {
      const anchorAt = rest.findIndex((r) => r.id === anchorId);
      if (anchorAt < 0) return;
      at = position === 'before' ? anchorAt : anchorAt + 1;
    }

    const wasAt = new Map<string, number>();
    rows.value.forEach((r, i) => wasAt.set(r.id, i));

    rows.value = orderSectionRows([...rest.slice(0, at), placed, ...rest.slice(at)]);
    reindex();

    const pairs: Array<[number, number]> = [];
    rows.value.forEach((r, i) => {
      const was = wasAt.get(r.id);
      if (was !== undefined && was !== i) pairs.push([was, i]);
    });
    if (pairs.length > 0) rowRemap.value = { pairs, seq: ++shiftSeq };
  }

  /**
   * Перетаскивание, сделанное другим человеком.
   *
   * Сервер прислал пары «строка → новый номер». Проставляем их у себя
   * и раскладываем окно по номеру: применение частичного набора безопасно —
   * непереставленные строки отрезка съехали на один слот в одну сторону,
   * их взаимный порядок сохранился.
   *
   * Раскладка идёт ТОЛЬКО когда сортировка своя, по номеру: при чужой
   * порядок задаёт не номер, и трогать его нельзя.
   */
  function applyRemoteMove(pairs: Array<{ id: string; rowNumber: number }>): void {
    const here = new Set(rows.value.map((r) => r.id));
    const known = pairs.filter((p) => here.has(p.id));
    if (known.length === 0) return;

    const byId = new Map(known.map((p) => [p.id, p.rowNumber]));
    const wasAt = new Map<string, number>();
    rows.value.forEach((r, i) => wasAt.set(r.id, i));

    const next = rows.value.map((r) =>
      byId.has(r.id) ? ({ ...r, rowNumber: byId.get(r.id)! } as CandidateRow) : r,
    );

    if (!manualOrderAvailable.value) {
      rows.value = next;
      reindex();
      return;
    }

    /*
      Внутри своей группы порядок задаёт номер — по нему и раскладываем.
      Группы при этом остаются на своих местах: их порядок задала загрузка.

      Раскладываем ТОЛЬКО те группы, чьи номера пришли в парах. Раскладка
      всех подряд отменяла бы намеренную расстановку `prependRow`:
      заведённая строка встаёт наверх своей группы, а номер у неё
      наибольший — и чужое перетаскивание в соседней группе роняло бы её
      в самый низ.
    */
    const field = groupBy.value;
    const touched = new Set(
      known.map((p) => {
        const r = next.find((x) => x.id === p.id)!;
        return field ? groupKeyOf(r, field) : '';
      }),
    );
    const buckets = new Map<string, CandidateRow[]>();
    const order: string[] = [];
    for (const r of next) {
      const key = field ? groupKeyOf(r, field) : '';
      if (!buckets.has(key)) {
        buckets.set(key, []);
        order.push(key);
      }
      buckets.get(key)!.push(r);
    }
    for (const [key, bucket] of buckets) {
      if (touched.has(key)) bucket.sort((x, y) => x.rowNumber - y.rowNumber);
    }

    if (field === 'sectionId') order.sort(dateGroupComparator(sections.value, next, EMPTY_GROUP_KEY, dateSectionOrder.value));
    rows.value = orderSectionRows(order.flatMap((key) => buckets.get(key)!));
    reindex();

    const remap: Array<[number, number]> = [];
    rows.value.forEach((r, i) => {
      const was = wasAt.get(r.id);
      if (was !== undefined && was !== i) remap.push([was, i]);
    });
    if (remap.length > 0) rowRemap.value = { pairs: remap, seq: ++shiftSeq };
  }

  /** Перемещение с отправкой на сервер. Ошибка возвращает строку на место. */
  async function moveRow(
    movedId: string,
    anchorId: string | null,
    position: 'before' | 'after',
    groupKey: string | null,
    options: { groupBy?: string | null; history?: CandidateMove['history'] } = {},
  ): Promise<{ ok: boolean; movedToGroup: boolean; departure?: DepartureGroupState }> {
    await waitForSaves();
    const row = rowById(movedId) ?? await api.get<CandidateRow>(`/candidates/${movedId}`);
    if (!row) return { ok: false, movedToGroup: false };

    const field = options.groupBy === undefined ? groupBy.value : options.groupBy;
    const back = placeOf(movedId, field);
    const movedToGroup = Boolean(field && groupKeyOf(row, field) !== (groupKey ?? EMPTY_GROUP_KEY));
    const version = row.version;

    if (groupBy.value === field) {
      moveRowLocally(movedId, anchorId, position, groupKey, field, options.history?.values);
    }

    try {
      const res = await api.post<{
        pairs: Array<{ id: string; rowNumber: number }>;
        cell: {
          field: string;
          value: unknown;
          derived: Record<string, unknown>;
          version: number;
        } | null;
        orderSkipped: boolean;
        version: number;
      }>('/candidates/move', {
        movedId,
        anchorId,
        position,
        groupBy: field,
        groupKey,
        version,
        clientOpId: newOpId(),
        ...(options.history ? { history: options.history } : {}),
      });

      /*
        Пары «строка → номер» применяем и к себе.

        Своё эхо по шине не придёт — оно отсекается по автору, — а без
        номеров колонка «№» показывает старые: без группировки там стоит
        книжный номер, и после перетаскивания он врал до F5. Заодно это
        сверка с сервером: место считает он, по полной группе, и под
        фильтром его ответ может разойтись с тем, что клиент нарисовал
        по видимым соседям. Прав сервер.
      */
      // Подписи и версию проставляем из ответа
      const changed = {
        ...row,
        ...(res.cell ? { [res.cell.field]: res.cell.value, ...res.cell.derived } : {}),
        version: res.version,
      } as CandidateRow;
      const idx = rowIndexById.get(movedId);
      if (idx !== undefined) {
        const current = rows.value[idx]!;
        replaceAt(idx, {
          ...current,
          ...(res.cell ? { [res.cell.field]: res.cell.value, ...res.cell.derived } : {}),
          version: res.version,
        } as CandidateRow);
      }
      if (res.pairs.length > 0) applyRemoteMove(res.pairs);
      const changedDateGroup = field === 'sectionId' || field === 'plannedDepartureDate' || res.cell?.derived?.sectionId !== undefined;
      if (changedDateGroup && hasDateVisibility.value) await refreshAfterDateChange();
      else if (field === 'sectionId') refreshDepartureSections();

      if (res.orderSkipped) {
        toasts.push({
          kind: 'info',
          title: field === 'sectionId' ? 'Группа сохранена' : 'Перенос сохранён',
          message: 'Прежний номер строки сохранён',
        });
        /*
          Строка сейчас нарисована там, куда её бросили, а сервер её туда
          не ставил: у всех остальных она стоит по своему номеру, и после
          F5 переедет и у автора. Экран, который врёт до перезагрузки, —
          худшее из состояний. Путь редкий (больше пяти тысяч строк),
          перечитать окно здесь ничего не стоит.
        */
        await reload();
      }
      else if (groupBy.value !== field) await reload();

      return { ok: true, movedToGroup, departure: departureState(changed) };
    } catch (e) {
      if (back && groupBy.value === field) moveRowLocally(movedId, back.anchorId, back.position, back.groupKey, field);
      // Возвращаем только поле группировки и его подпись.
      const idx = rowIndexById.get(movedId);
      if (idx !== undefined && field) {
        const rollbackFields = historyFields([field, ...(GROUP_LABEL_FIELDS[field] ?? [])]);
        if (field === 'sectionId') rollbackFields.push('sectionLabel');
        replaceAt(idx, {
          ...rows.value[idx]!,
          ...Object.fromEntries(rollbackFields.map((key) => [key, (row as unknown as Record<string, unknown>)[key] ?? null])),
        });
      }
      toasts.push({
        kind: 'error',
        title: 'Не удалось переставить строку',
        message: e instanceof ApiError ? e.message : undefined,
      });
      return { ok: false, movedToGroup: false };
    }
  }

  /** Один перенос — одна атомарная отмена группы и места строки. */
  async function moveRowWithUndo(
    movedId: string,
    anchorId: string | null,
    position: 'before' | 'after',
    groupKey: string | null,
    fieldOverride = groupBy.value,
  ) {
    const recordUndo = history.capture();
    const field = fieldOverride;
    await waitForSaves();
    const row = rowById(movedId);
    const back = placeOf(movedId, field);
    const name = row?.fullName ?? 'Строка';
    let before = row ? departureState(row) : undefined;
    const result = await moveRow(movedId, anchorId, position, groupKey, { groupBy: field });
    if (!result.ok || !back || !before) return result;
    let after = result.departure ?? before;
    const linkedDate = field === 'sectionId' || field === 'plannedDepartureDate';
    const replay = async (reverse: boolean) => {
      const target = reverse ? back : { anchorId, position, groupKey };
      const r = await moveRow(movedId, target.anchorId, target.position, target.groupKey, {
        groupBy: field,
        ...(linkedDate ? { history: {
          expected: reverse ? after : before!,
          values: reverse ? before! : after,
        } } : {}),
      });
      if (!r.ok) throw new Error('Не удалось повторить перемещение');
      if (r.departure) {
        if (reverse) before = r.departure;
        else after = r.departure;
      }
    };
    recordUndo({
      label: result.movedToGroup ? `Перенос строки «${name}»` : `Перестановка строки «${name}»`,
      undo: () => replay(true),
      redo: () => replay(false),
    });
    return result;
  }

  /**
   * Правка кандидата, строки которого нет в загруженной части таблицы.
   *
   * Грид держит в памяти двести строк, а карточку открывают и по прямой
   * ссылке, и после перезагрузки страницы, и из поиска — кандидат при этом
   * запросто оказывается на четырёхсотой позиции. Раньше такая правка молча
   * отклонялась: patchCell не находил строку и возвращал отказ, карточка
   * возвращала прежнее значение, и человек видел, как набранное «само
   * стирается». Сообщения при этом не было никакого.
   *
   * Версию берём из самой карточки: она читала кандидата с сервера и знает
   * её точно. Оптимистичного обновления здесь нет — обновлять нечего.
   */
  async function patchDetached(
    rowId: string,
    field: string,
    value: unknown,
    version: number,
    original: CandidateRow,
  ): Promise<CellPatchResult> {
    const before = cellSnapshot(original, field);
    try {
      const res = await api.patch<CellPatchResponse>(`/candidates/${rowId}/cell`, {
        field,
        value,
        version,
        clientOpId: newOpId(),
      });
      const after = cellSnapshot({ ...original, [field]: res.value, ...res.derived }, field);
      if (field === 'plannedDepartureDate' || field === 'sectionId') await refreshAfterDateChange();
      return { ok: true, previous: before[field], version: res.version, before, after };
    } catch (e) {
      toasts.error(
        'Не удалось сохранить',
        e instanceof ApiError ? e.message : 'Проверьте соединение',
      );
      return { ok: false, previous: undefined };
    }
  }

  const saveQueue = new Map<string, Promise<CellPatchResult>>();

  async function waitForCellSaves() { await Promise.allSettled([...saveQueue.values()]); }

  async function waitForSaves() { await Promise.allSettled([waitForCellSaves(), rowColorQueue, cellBoldQueue]); }

  function patchCell(rowId: string, field: string, value: unknown, options: { silent?: boolean; version?: number } = {}): Promise<CellPatchResult> {
    const predecessor = saveQueue.get(rowId);
    const save = (predecessor ?? Promise.resolve()).then((previous) => patchCellNow(rowId, field, value, {
      ...options,
      // Карточка вне загруженной страницы ещё показывает версию до
      // предыдущего сохранения. Очередь знает версию именно его ответа.
      ...(previous?.ok && previous.version !== undefined ? { version: previous.version } : {}),
    })).catch(() => {
      if (!options.silent) toasts.error('Не удалось сохранить', 'Проверьте соединение с сервером');
      return { ok: false, previous: undefined };
    });
    saveQueue.set(rowId, save);
    void save.then(() => { if (saveQueue.get(rowId) === save) saveQueue.delete(rowId); });
    return save;
  }

  /** Object and its free text are one cell, one revision and one undo step. */
  function patchWorkObject(rowId: string, value: WorkObjectValue, options: { silent?: boolean; version?: number } = {}): Promise<CellPatchResult> {
    const predecessor = saveQueue.get(rowId);
    const normalized = { workObjectId: value.workObjectId, workObjectNote: value.workObjectNote?.trim() || null };
    const save = (predecessor ?? Promise.resolve()).then((previous) => patchWorkObjectNow(rowId, normalized, {
      ...options,
      ...(previous?.ok && previous.version !== undefined ? { version: previous.version } : {}),
    })).catch(() => {
      if (!options.silent) toasts.error('Не удалось сохранить', 'Проверьте соединение с сервером');
      return { ok: false, previous: undefined };
    });
    saveQueue.set(rowId, save);
    void save.then(() => { if (saveQueue.get(rowId) === save) saveQueue.delete(rowId); });
    return save;
  }

  async function patchWorkObjectNow(rowId: string, value: WorkObjectValue, options: { silent?: boolean; version?: number }): Promise<CellPatchResult> {
    const index = rowIndexById.get(rowId);
    const original = index === undefined ? await api.get<CandidateRow>(`/candidates/${rowId}`) : rows.value[index];
    if (!original) return { ok: false, previous: undefined };
    const before: WorkObjectValue = { workObjectId: original.workObjectId ?? null, workObjectNote: original.workObjectNote ?? null };
    if (before.workObjectId === value.workObjectId && before.workObjectNote === value.workObjectNote) {
      return { ok: true, previous: before, version: original.version, before: { ...before }, after: { ...before } };
    }
    const expectedVersion = options.version ?? original.version;
    const optimistic = index !== undefined && expectedVersion === original.version;
    if (optimistic && index !== undefined) replaceAt(index, { ...original, ...value });
    markSaving(rowId, 'workObjectId');
    try {
      const response = await api.patch<WorkObjectPatchResponse>(`/candidates/${rowId}/work-object`, {
        ...value, version: expectedVersion, clientOpId: newOpId(),
      });
      const currentIndex = rowIndexById.get(rowId);
      if (currentIndex !== undefined && rows.value[currentIndex]!.version <= response.version) replaceAt(currentIndex, {
        ...rows.value[currentIndex]!, ...response.after, ...response.derived, version: response.version, updatedAt: response.updatedAt,
      });
      markSaved(rowId, 'workObjectId');
      return { ok: true, previous: response.before, version: response.version, before: { ...response.before }, after: { ...response.after } };
    } catch (error) {
      const currentIndex = rowIndexById.get(rowId);
      if (optimistic && currentIndex !== undefined && rows.value[currentIndex]!.version === original.version) {
        replaceAt(currentIndex, { ...rows.value[currentIndex]!, ...before });
      }
      markError(rowId, 'workObjectId');
      if (!options.silent) explainPatchError(error, 'workObjectNote');
      return { ok: false, previous: before };
    }
  }

  async function patchCellNow(
    rowId: string,
    field: string,
    value: unknown,
    options: { silent?: boolean; version?: number } = {},
  ): Promise<CellPatchResult> {
    const index = rowIndexById.get(rowId);
    if (index === undefined) {
      const fresh = await api.get<CandidateRow>(`/candidates/${rowId}`);
      return patchDetached(rowId, field, value, options.version ?? fresh.version, fresh);
    }

    const row = rows.value[index];
    if (!row) return { ok: false, previous: undefined };

    const previous = storedCellValue(row, field);
    const before = cellSnapshot(row, field);
    if (previous === value) return { ok: true, previous, version: row.version, before, after: before };

    // Локально применяем сразу — иначе ввод «дёргается» на каждой ячейке
    replaceAt(index, { ...row, [field]: value, ...(field === 'age' ? { ageOverride: value } : {}) } as CandidateRow);
    markSaving(rowId, field);

    try {
      const res = await api.patch<CellPatchResponse>(`/candidates/${rowId}/cell`, {
        field,
        value,
        version: row.version,
        clientOpId: newOpId(),
      });
      // Снимок относится к ответу этого запроса, даже если следующая
      // правка уже ждёт в очереди или начала оптимистичное обновление.
      const after = cellSnapshot({ ...row, [field]: res.value, ...res.derived }, field);

      const idx = rowIndexById.get(rowId);
      if (idx !== undefined) {
        replaceAt(idx, {
          ...(rows.value[idx] as CandidateRow),
          [field]: res.value,
          ...res.derived,
          version: res.version,
          updatedAt: res.updatedAt,
        } as CandidateRow);
      }

      markSaved(rowId, field);
      if (field === 'plannedDepartureDate' || field === 'sectionId') await refreshAfterDateChange();
      else if (genderFilterAffected(field)) await refreshAfterCommittedChange('Кандидат сохранён, отбор по полу не обновился');
      return { ok: true, previous, version: res.version, before, after };
    } catch (e) {
      // Откат к тому, что было до правки
      const idx = rowIndexById.get(rowId);
      if (idx !== undefined) {
        replaceAt(idx, { ...(rows.value[idx] as CandidateRow), [field]: row[field as keyof CandidateRow],
          ...(field === 'age' ? { ageOverride: row.ageOverride } : {}) } as CandidateRow);
      }
      markError(rowId, field);

      if (!options.silent) explainPatchError(e, field);
      return { ok: false, previous };
    }
  }

  function explainPatchError(e: unknown, field: string): void {
    if (!(e instanceof ApiError)) {
      toasts.error('Не удалось сохранить', 'Проверьте соединение с сервером');
      return;
    }

    switch (e.code) {
      case 'VERSION_CONFLICT': {
        // Кто-то успел раньше. Подсовываем актуальную строку и говорим об этом
        const current = e.details?.current as CandidateRow | undefined;
        if (current) replaceRow(current);
        toasts.warning(
          'Строку изменил другой пользователь',
          'Показано актуальное значение. Повторите правку, если она всё ещё нужна.',
        );
        break;
      }
      case 'CITY_COUNTRY_MISMATCH':
        toasts.warning(
          'Город не подходит под гражданство',
          'Сначала измените гражданство или выберите город нужной страны.',
        );
        break;
      case 'CANDIDATE_DELETED':
        toasts.warning('Кандидат удалён', 'Строку удалил другой пользователь.');
        break;
      case 'FORBIDDEN':
        toasts.error('Недостаточно прав', 'Изменение данных недоступно для вашей роли.');
        break;
      case 'VALIDATION_ERROR':
        toasts.error('Значение не принято', e.fields?.[field] ?? e.message);
        break;
      default:
        toasts.error('Не удалось сохранить', e.message);
    }
  }

  function replaceRow(row: CandidateRow): void {
    const idx = rowIndexById.get(row.id);
    const previous = idx === undefined ? undefined : rows.value[idx];
    if (hasGenderFilter() && (!previous || previous.fullName !== row.fullName || previous.gender !== row.gender)) {
      void reload();
      return;
    }
    if (hasDateVisibility.value && (idx === undefined || rows.value[idx]?.sectionId !== row.sectionId)) {
      void reload();
      return;
    }
    if (idx === undefined) return;
    replaceAt(idx, row);
  }

  /** Применение чужой правки, пришедшей по WebSocket. */
  function applyRemoteCell(
    rowId: string,
    field: string,
    value: unknown,
    derived: Record<string, unknown> | undefined,
    version: number,
  ): void {
    if (genderFilterAffected(field)) { void reload(); return; }
    const nextSectionId = field === 'sectionId' ? value : derived?.sectionId;
    if (hasDateVisibility.value && (field === 'sectionId' || field === 'plannedDepartureDate' || nextSectionId !== undefined)) {
      void reload();
      return;
    }
    if (typeof nextSectionId === 'string' &&
      !sections.value.some((section) => section.id === nextSectionId)) refreshDepartureSections();
    const idx = rowIndexById.get(rowId);
    if (idx === undefined) return;

    const row = rows.value[idx];
    if (!row) return;
    // Наше собственное эхо: версия уже применена, мигать не нужно
    if (row.version >= version) return;

    replaceAt(idx, { ...row, [field]: value, ...(derived ?? {}), version } as CandidateRow);
    markRemoteChanged(rowId, field);
  }

  function removeRow(rowId: string): void {
    const idx = rowIndexById.get(rowId);
    if (idx === undefined) return;
    rows.value = rows.value.filter((r) => r.id !== rowId);
    reindex();
    noteShift(idx, -1);
  }

  /** Новые строки дописываются снизу своей группы. */
  function prependRow(row: CandidateRow): void {
    if (needsDateVisibilityCheck(row)) { void reload(); return; }
    if (row.sectionId && !sections.value.some((section) => section.id === row.sectionId)) refreshDepartureSections();
    if (rowIndexById.has(row.id)) return;

    const preferred = groupEndFor(row);
    rows.value = orderSectionRows([...rows.value.slice(0, preferred), row, ...rows.value.slice(preferred)]);
    const at = rows.value.findIndex((item) => item.id === row.id);
    reindex();
    total.value += 1;
    noteShift(at, 1);
  }

  function needsDateVisibilityCheck(row: CandidateRow): boolean {
    return hasGenderFilter() || hasDateVisibility.value && Boolean(row.sectionId)
      && !sections.value.some((section) => section.id === row.sectionId);
  }

  function hasGenderFilter(): boolean {
    return filters.value.some((filter) => filter.field === 'resolvedGender');
  }

  function genderFilterAffected(field: string): boolean {
    return hasGenderFilter() && (field === 'gender' || field === 'fullName' || field === 'middleName');
  }

  /** Конец своей группы; новая группа даты встаёт по выбранному порядку. */
  function groupEndFor(row: CandidateRow): number {
    const field = groupBy.value;
    if (!field) return rows.value.length;

    const key = groupKeyOf(row, field);
    const at = rows.value.findLastIndex((r) => groupKeyOf(r, field) === key);
    if (at < 0 && field === 'sectionId') {
      const compare = dateGroupComparator(sections.value, [...rows.value, row], EMPTY_GROUP_KEY, dateSectionOrder.value);
      const before = rows.value.findIndex((existing) => compare(key, groupKeyOf(existing, field)) < 0);
      return before < 0 ? rows.value.length : before;
    }
    return at < 0 ? rows.value.length : at + 1;
  }

  function rowById(id: string): CandidateRow | undefined {
    const idx = rowIndexById.get(id);
    return idx === undefined ? undefined : rows.value[idx];
  }

  // ── Массовые операции ───────────────────────────────────

  /**
   * Правка одного поля у множества строк.
   *
   * Возвращает предыдущие значения, чтобы стек отмены мог вернуть
   * каждую строку к своему состоянию, а не к общему.
   */
  async function bulkUpdate(
    ids: string[],
    patch: Record<string, unknown>,
  ): Promise<{ ok: boolean; historyRows: CandidateHistoryRow[] }> {
    await waitForSaves();

    try {
      const result = await api.post<{ historyRows: CandidateHistoryRow[] }>('/candidates/bulk-update', {
        ids,
        patch,
        clientOpId: newOpId(),
        // Строки могли измениться, пока пользователь выбирал действие;
        // операция на полтысячи строк не должна падать целиком из-за одной
        force: true,
      });

      // Перечитываем окно: сервер мог пересчитать производные поля
      await reload();
      return { ok: true, historyRows: result.historyRows ?? [] };
    } catch (e) {
      toasts.error(
        'Массовая правка не применена',
        e instanceof ApiError ? e.message : 'Проверьте соединение',
      );
      return { ok: false, historyRows: [] };
    }
  }

  type RowColorSnapshot = Map<string, string | null>;
  let cellBoldQueue: Promise<unknown> = Promise.resolve();

  /** Общий стиль ячейки сохраняется отдельно от значений и версии строки. */
  function setCellsBold(ids: string[], fields: string[], bold: boolean | null, record = true): Promise<boolean> {
    if (observationActive.value || !ids.length || ids.length > 1000 || !fields.length) return Promise.resolve(false);
    const recordUndo = history.capture();
    const apply = cellBoldQueue.then(async () => {
      await waitForCellSaves();
      try {
        const result = await api.patch<{ ids: string[]; previous: CandidateCellBoldSnapshot[] }>(
          '/candidates/cell-bold', { ids: [...new Set(ids)], fields: [...new Set(fields)], bold, clientOpId: newOpId() },
        );
        for (const id of result.ids) {
          const index = rowIndexById.get(id);
          const current = index === undefined ? undefined : rows.value[index];
          if (!current || index === undefined) continue;
          const cellBold = { ...current.cellBold };
          for (const field of fields) {
            if (bold === null) delete cellBold[field];
            else cellBold[field] = bold;
          }
          replaceAt(index, { ...current, cellBold });
        }
        const previous = result.previous.map((row) => ({
          id: row.id,
          values: Object.fromEntries(Object.entries(row.values).filter(([, value]) => value !== bold)),
        })).filter((row) => Object.keys(row.values).length);
        if (record && previous.length) {
          const after = previous.map((row) => ({
            id: row.id, values: Object.fromEntries(Object.keys(row.values).map((field) => [field, bold])),
          }));
          recordUndo({
            label: bold ? 'Жирный текст' : 'Обычный текст',
            undo: () => restoreCellBold(previous),
            redo: () => restoreCellBold(after),
          });
        }
        return true;
      } catch (error) {
        toasts.error('Начертание не сохранено', error instanceof ApiError ? error.message : 'Проверьте соединение');
        return false;
      }
    });
    cellBoldQueue = apply.catch(() => {});
    return apply;
  }

  async function restoreCellBold(snapshot: CandidateCellBoldSnapshot[]): Promise<void> {
    const groups = new Map<string, { ids: string[]; fields: string[]; bold: boolean | null }>();
    for (const row of snapshot) {
      for (const bold of [null, false, true]) {
        const fields = Object.keys(row.values).filter((field) => row.values[field] === bold).sort();
        if (!fields.length) continue;
        const key = JSON.stringify([bold, fields]);
        const group = groups.get(key) ?? { ids: [], fields, bold };
        group.ids.push(row.id);
        groups.set(key, group);
      }
    }
    for (const group of groups.values()) {
      if (!await setCellsBold(group.ids, group.fields, group.bold, false)) {
        throw new Error('Не удалось вернуть прежнее начертание');
      }
    }
  }

  let rowColorQueue: Promise<unknown> = Promise.resolve();

  /** Цвет строки не меняет данные кандидата, ручные цвета ячеек и порядок строк. */
  function colorRows(ids: string[], color: string | null, record = true): Promise<boolean> {
    if (observationActive.value || !ids.length || ids.length > 1000) return Promise.resolve(false);
    const recordUndo = history.capture();
    const apply = rowColorQueue.then(async () => {
      await waitForCellSaves();
      const previous: RowColorSnapshot = new Map();
      for (const id of new Set(ids)) {
        const row = rowById(id);
        if (row) previous.set(id, row.rowColor ?? null);
      }
      try {
        const result = await api.patch<{ items: Array<{ id: string; rowColor: string | null }> }>(
          '/candidates/row-color', { ids: [...new Set(ids)], color, clientOpId: newOpId() },
        );
        const changed = new Set(result.items.map((item) => item.id));
        for (const item of result.items) {
          const index = rowIndexById.get(item.id);
          const current = index === undefined ? undefined : rows.value[index];
          if (current && index !== undefined) replaceAt(index, { ...current, rowColor: item.rowColor });
        }
        for (const [id, before] of previous) {
          if (!changed.has(id) || before === color) previous.delete(id);
        }
        if (record && previous.size) recordUndo({
          label: color ? 'Заливка строки' : 'Заливка строки снята',
          undo: async () => { await restoreRowColors(previous); },
          redo: async () => {
            if (!await colorRows([...previous.keys()], color, false)) throw new Error('Не удалось повторить заливку строк');
          },
        });
        return true;
      } catch (error) {
        toasts.error('Заливка строки не сохранена', error instanceof ApiError ? error.message : 'Проверьте соединение');
        return false;
      }
    });
    rowColorQueue = apply.catch(() => {});
    return apply;
  }

  async function restoreRowColors(previous: RowColorSnapshot): Promise<void> {
    const groups = new Map<string | null, string[]>();
    for (const [id, color] of previous) {
      const ids = groups.get(color) ?? [];
      ids.push(id);
      groups.set(color, ids);
    }
    for (const [color, ids] of groups) {
      if (!await colorRows(ids, color, false)) throw new Error('Не удалось вернуть прежние цвета строк');
    }
  }

  /** Прежние цвета выделения: строка → поле → цвет (`null` — не покрашено). */
  type CellColorSnapshot = Map<string, Record<string, string | null>>;

  function applyColorsLocally(ids: string[], fields: string[], color: string | null): void {
    for (const id of ids) {
      const index = rowIndexById.get(id);
      if (index === undefined) continue;
      const row = rows.value[index];
      if (!row) continue;

      const next = { ...(row.cellColors ?? {}) };
      for (const field of fields) {
        if (color) next[field] = color;
        else delete next[field];
      }
      replaceAt(index, { ...row, cellColors: next } as CandidateRow);
    }
  }

  /**
   * Заливка выделенных ячеек.
   *
   * Применяется сразу, не дожидаясь сервера: человек красит выделение
   * из десяти колонок и ждёт цвета, а не подтверждения. Не сохранилось —
   * цвет откатывается к прежнему, и об этом говорит уведомление.
   *
   * Перечитывать таблицу, как после массовой правки, незачем: производных
   * полей у цвета нет, и сервер возвращает ровно то, что мы уже нарисовали.
   *
   * Возвращает прежние цвета — по каждой ячейке свой: в выделении они
   * могли быть разными, и «отменить» не значит «очистить».
   */
  async function colorCells(
    ids: string[],
    fields: string[],
    color: string | null,
  ): Promise<{ ok: boolean; previous: CellColorSnapshot }> {
    const previous: CellColorSnapshot = new Map();
    for (const id of ids) {
      const row = rowById(id);
      if (!row) continue;
      const before: Record<string, string | null> = {};
      for (const field of fields) before[field] = row.cellColors?.[field] ?? null;
      previous.set(id, before);
    }

    applyColorsLocally(ids, fields, color);

    try {
      await api.post('/candidates/bulk-color', { ids, fields, color, clientOpId: newOpId() });
      return { ok: true, previous };
    } catch (e) {
      for (const [id, before] of previous) {
        for (const [field, prev] of Object.entries(before)) {
          applyColorsLocally([id], [field], prev);
        }
      }
      toasts.error(
        'Заливка не сохранена',
        e instanceof ApiError ? e.message : 'Проверьте соединение',
      );
      return { ok: false, previous };
    }
  }

  /**
   * Вернуть ячейкам прежние цвета — для отмены.
   *
   * Один запрос на каждое сочетание «цвет + набор колонок», а не на строку:
   * обычный случай — всё выделение было одного цвета (чаще всего никакого),
   * и тогда запрос ровно один.
   *
   * Неудача выбрасывается наружу, а не проглатывается. `colorCells` ловит
   * свои ошибки сам и возвращает `ok: false`; молча вернуть управление
   * значило бы отчитаться «Отменено», ничего не отменив, — тот же случай,
   * от которого защищается отмена массовых правок.
   */
  async function restoreCellColors(previous: CellColorSnapshot): Promise<void> {
    /*
      Пустой снимок — это не «нечего делать», а «строк, которых касалась
      покраска, больше нет в загруженной выборке»: их отфильтровали,
      перечитали таблицу, ушли на другой срез.
    */
    if (previous.size === 0) {
      throw new Error('Строки, которых касалась заливка, больше не загружены');
    }

    const groups = new Map<string, { ids: string[]; fields: string[]; color: string | null }>();

    for (const [id, byField] of previous) {
      const fieldsByColor = new Map<string, string[]>();
      for (const [field, color] of Object.entries(byField)) {
        const key = color ?? '';
        const list = fieldsByColor.get(key) ?? [];
        list.push(field);
        fieldsByColor.set(key, list);
      }

      for (const [key, fields] of fieldsByColor) {
        const sorted = [...fields].sort();
        const signature = `${key}|${sorted.join(',')}`;
        const group = groups.get(signature) ?? { ids: [], fields: sorted, color: key || null };
        group.ids.push(id);
        groups.set(signature, group);
      }
    }

    const results = [];
    for (const group of groups.values()) {
      results.push(await colorCells(group.ids, group.fields, group.color));
    }
    if (results.some((r) => !r.ok)) {
      throw new Error('Не удалось вернуть прежние цвета');
    }
  }

  /**
   * Заведение кандидата.
   *
   * Новая строка добавляется снизу своей группы. Вызывающий грид переводит
   * выделение на её ID, чтобы можно было сразу продолжить ввод.
   */
  async function createCandidate(
    payload: Record<string, unknown>,
    options: { ignoreDuplicates?: boolean; ignoreBlacklist?: boolean; ignoreComments?: boolean } = {},
  ): Promise<
    { ok: true; row: CandidateRow; duplicates: DuplicateMatch[] } | { ok: false; error: ApiError | null }
  > {
    const recordUndo = history.capture();
    try {
      // Ответ приходит конвертом: строка плюс найденные (но принятые) дубли
      const res = await api.post<{
        row: CandidateRow; duplicates?: DuplicateMatch[]; travelMateChanges?: CandidateTravelMateChange[];
      }>(
        '/candidates',
        {
          ...payload,
          ignoreDuplicates: options.ignoreDuplicates ?? false,
          ignoreBlacklist: options.ignoreBlacklist ?? false,
          ignoreComments: options.ignoreComments ?? false,
          clientOpId: newOpId(),
        },
      );

      // Свой WebSocket-echo пропускается по clientOpId: изменения других
      // участников той же операции применяем из HTTP-ответа на их местах.
      for (const change of res.travelMateChanges ?? []) {
        const { id, version, travelGroupId, oldTravelGroupId: _oldGroupId, ...derived } = change;
        applyRemoteCell(id, 'travelGroupId', travelGroupId, derived, version);
      }
      // После успешного POST создание состоялось: ошибка чтения таблицы
      // не должна разрешать повторное создание с новым clientOpId.
      recordCreatedRow(res.row.id, recordUndo, res.travelMateChanges);
      if (needsDateVisibilityCheck(res.row)) await refreshAfterCommittedChange('Кандидат заведён, таблица не обновилась');
      else prependRow(res.row);
      return { ok: true, row: res.row, duplicates: res.duplicates ?? [] };
    } catch (e) {
      return { ok: false, error: e instanceof ApiError ? e : null };
    }
  }

  /**
   * Вставка строки между соседями — «как в Excel».
   *
   * Сервер сдвигает номера всех строк ниже места вставки, поэтому таблицу
   * приходится перечитать целиком: у половины строк изменился номер,
   * а он же — ключ сортировки по умолчанию.
   */
  async function insertCandidate(
    payload: Record<string, unknown> & { anchorRowNumber: number; position: 'above' | 'below' },
  ): Promise<{ ok: true; row: CandidateRow } | { ok: false; error: ApiError | null }> {
    const recordUndo = history.capture();
    try {
      const res = await api.post<{ row: CandidateRow }>('/candidates/insert', {
        ...payload,
        ignoreDuplicates: true,
        ignoreBlacklist: true,
        clientOpId: newOpId(),
      });

      if (needsDateVisibilityCheck(res.row)) await reload();
      else insertRowLocally(res.row, payload.anchorRowNumber, payload.position);
      recordCreatedRow(res.row.id, recordUndo);
      return { ok: true, row: res.row };
    } catch (e) {
      return { ok: false, error: e instanceof ApiError ? e : null };
    }
  }

  /**
   * Вставка строки в загруженный список — без перезагрузки таблицы.
   *
   * Сначала здесь стоял `reload()`, и это было видно: список обнулялся,
   * на его месте показывался скелет, виртуализатор пересоздавал все строки,
   * прокрутка прыгала в начало. Таблица мигала целиком ради одной строки.
   *
   * Сервер при вставке сдвинул row_number у всех строк ниже — ровно то же
   * делаем в памяти. Внутри группы вставляем рядом с якорем: в общем списке
   * перед ним могут быть строки других групп с большими row_number.
   */
  function insertRowLocally(row: CandidateRow, anchorRowNumber: number, position: 'above' | 'below'): void {
    if (needsDateVisibilityCheck(row)) { void reload(); return; }
    if (row.sectionId && !sections.value.some((section) => section.id === row.sectionId)) refreshDepartureSections();
    if (rowIndexById.has(row.id)) return;

    const anchorIndex = rows.value.findIndex((existing) => existing.rowNumber === anchorRowNumber);
    // Номера ниже места вставки сдвинулись на сервере — повторяем сдвиг,
    // иначе таблица разойдётся с базой до следующей перезагрузки.
    const next = rows.value.map((existing) => existing.rowNumber >= row.rowNumber
      ? ({ ...existing, rowNumber: existing.rowNumber + 1 } as CandidateRow)
      : existing);
    const insertAt = anchorIndex >= 0
      ? anchorIndex + (position === 'below' ? 1 : 0)
      : next.findIndex((existing) => existing.rowNumber >= row.rowNumber);
    next.splice(insertAt < 0 ? next.length : insertAt, 0, row);

    rows.value = orderSectionRows(next);
    reindex();
    total.value += 1;
    // Номера строк ниже места вставки сдвинулись — вместе с ними едет
    // и выделение, которое этими номерами и живёт
    noteShift(rowIndexById.get(row.id) ?? 0, 1);
  }

  /**
   * Сверка с чёрным списком — по мере ввода, рядом с проверкой дублей.
   *
   * Вопросы разные: дубль значит «его уже заводили», чёрный список —
   * «с ним не работаем, и вот почему». Поэтому и запрос отдельный,
   * и показывается результат отдельно.
   */
  async function checkBlacklist(params: BlacklistCheck): Promise<BlacklistMatch[]> {
    try {
      const res = await api.post<{ matches: BlacklistMatch[] }>('/blacklist/check', params);
      return res.matches;
    } catch {
      // Сверка — подсказка, а не преграда: её отказ не должен мешать работе
      return [];
    }
  }

  /** Старые комментарии — подсказка; создание повторяет сверку на сервере. */
  async function checkComments(params: CandidateCommentCheck): Promise<CandidateCommentMatch[]> {
    try {
      const res = await api.post<{ matches: CandidateCommentMatch[] }>('/candidates/check-comments', params);
      return res.matches;
    } catch {
      return [];
    }
  }

  /** Поиск возможных дублей — вызывается по мере ввода телефона. */
  async function checkDuplicates(params: Partial<Record<PhoneQueryField, string>> & {
    fullName?: string;
    birthDate?: string;
  }): Promise<DuplicateMatch[]> {
    try {
      const res = await api.post<{ matches: DuplicateMatch[] }>(
        '/candidates/check-duplicates',
        params,
      );
      return res.matches;
    } catch {
      // Проверка дублей — подсказка, а не преграда: её отказ не должен
      // мешать завести кандидата
      return [];
    }
  }

  /**
   * Вставка блока значений одним запросом.
   *
   * Раньше грид слал по запросу на ячейку: блок 20×5 из Excel превращался
   * в сотню последовательных обращений, интерфейс подвисал, а при обрыве
   * на середине половина значений оставалась применённой.
   * Сервер делает то же самое одной транзакцией и возвращает отчёт,
   * по которому строится точная запись в стеке отмены.
   */
  async function pasteBlock(
    columns: string[],
    payloadRows: Array<{ id: string | null; version: number | null; values: Array<string | null>; workObject?: WorkObjectValue }>,
  ): Promise<PasteResult | null> {
    try {
      const res = await api.post<PasteResult>('/candidates/paste', {
        columns,
        rows: payloadRows,
        clientOpId: newOpId(),
      });
      await reload();
      return { ...res, historyRows: res.historyRows ?? [] };
    } catch (e) {
      toasts.error(
        'Вставка не применена',
        e instanceof ApiError ? e.message : 'Проверьте соединение с сервером',
      );
      return null;
    }
  }

  async function restoreRows(ids: string[]): Promise<void> {
    let needsReload = false;
    for (const id of ids) {
      const result = await api.post<{ row: CandidateRow }>(`/candidates/${id}/restore?clientOpId=${newOpId()}`);
      if (needsDateVisibilityCheck(result.row)) needsReload = true;
      else prependRow(result.row);
    }
    if (needsReload) await refreshAfterCommittedChange('Строки восстановлены, таблица не обновилась');
    if (sort.value.length === 1 && sort.value[0]?.field === 'rowNumber') {
      const previous = rows.value;
      const direction = sort.value[0].dir === 'desc' ? -1 : 1;
      rows.value = [...rows.value].sort((left, right) => direction * (left.rowNumber - right.rowNumber));
      reindex();
      if (groupBy.value) regroupRows();
      rowRemap.value = { pairs: previous.map((row, index) => [index, rowIndexById.get(row.id)!]), seq: ++shiftSeq };
    }
  }

  async function captureFields(ids: string[], fields: string[]) {
    fields = historyFields(fields);
    const result = new Map<string, Record<string, unknown>>();
    for (let offset = 0; offset < ids.length; offset += 20) {
      await Promise.all(ids.slice(offset, offset + 20).map(async (id) => {
        const row = rowById(id) ?? await api.get<CandidateRow>(`/candidates/${id}`);
        result.set(id, Object.fromEntries(fields.map((field) => [field, storedCellValue(row, field) ?? null])));
      }));
    }
    return result;
  }

  async function compensateFields(changes: Array<{ id: string; expected: Record<string, unknown>; values: Record<string, unknown> }>) {
    const result = await api.post<{ rows: Array<{ id: string; values: Record<string, unknown> }> }>('/candidates/history', { rows: changes });
    const actual = new Map(result.rows.map((row) => [row.id, row.values]));
    await refreshAfterCommittedChange('Изменения сохранены, таблица не обновилась');
    return actual;
  }

  /** Сервер уже подтвердил запись; обновление окна — отдельное чтение. */
  async function refreshAfterCommittedChange(warning: string): Promise<void> {
    try {
      await reload();
      if (!error.value) return;
    } catch { /* Ошибка чтения не отменяет подтверждённую запись. */ }
    toasts.warning(warning, 'Обновите страницу, чтобы увидеть актуальные строки.');
  }

  function recordCreatedRow(id: string, recordUndo: UndoRecorder, travelMateChanges: CandidateTravelMateChange[] = []): void {
    const operation = createBatchHistory('Добавление строки', travelMateChanges.map((mate) => ({
      id: mate.id,
      before: { travelGroupId: mate.oldTravelGroupId },
      after: { travelGroupId: mate.travelGroupId },
    })), [id], {
      compensate: compensateFields,
      deleteCreated: async (ids) => {
        if (!await bulkDelete(ids, false)) throw new Error('Не удалось отменить добавление');
      },
      restoreCreated: restoreRows,
    });
    if (operation) recordUndo(operation);
  }

  async function bulkDelete(ids: string[], record = true): Promise<boolean> {
    const recordUndo = history.capture();
    try {
      await api.post('/candidates/bulk-delete', { ids, clientOpId: newOpId() });
      rows.value = rows.value.filter((r) => !ids.includes(r.id));
      reindex();
      total.value = Math.max(0, total.value - ids.length);
      if (record) recordUndo({
        label: `Удаление строк (${ids.length})`,
        undo: () => restoreRows(ids),
        redo: async () => { if (!await bulkDelete(ids, false)) throw new Error('Не удалось повторить удаление'); },
      });
      return true;
    } catch (e) {
      toasts.error(
        'Не удалось удалить',
        e instanceof ApiError ? e.message : 'Проверьте соединение',
      );
      return false;
    }
  }

  /**
   * Отметка о копировании в буфер.
   *
   * Значения не отправляются — только объём и состав выделения.
   * Подробности в сервисе на сервере (`logCopy`).
   */
  async function logCopy(payload: CopyLogPayload): Promise<void> {
    await api.post('/candidates/copy-log', payload);
  }

  return {
    observationActive,
    observationView,
    beginObservation,
    applyObservationView,
    applyObservationWindow,
    endObservation,
    discardObservation,
    // данные
    rows,
    hiddenGaps,
    loading,
    refreshing,
    hasLoadedRows,
    enter,
    loadingMore,
    initialised,
    error,
    total,
    hasMore,
    // представление
    sort,
    filters,
    search,
    includeDeleted,
    includeHidden,
    hiddenOnly,
    travelGroupFilter,
    rowShift,
    rowRemap,
    visibleColumnKeys,
    visibleColumns,
    pinnedColumns,
    pinnedRightColumns,
    pinnedRightKeys,
    pinnedRightWidth,
    scrollColumns,
    columnWidths,
    density,
    showRowNumbers,
    dateSectionOrder,
    setDateSectionOrder,
    cellLines,
    wrapText,
    sections,
    hasDateVisibility,
    captureFields,
    historyFields,
    waitForSaves,
    compensateFields,
    loadSections,
    addDateSection,
    updateDateSection,
    deleteDateSection,
    setRowSeparator,
    moveRowSeparator,
    groupBy,
    groupByLabel,
    groupableFields: VIEW_GROUPABLE_FIELDS,
    collapsedGroups,
    setGroupBy,
    toggleGroupCollapsed,
    groupKeyOf,
    EMPTY_GROUP_KEY,
    manualOrderAvailable,
    placeOf,
    moveRow,
    moveRowWithUndo,
    moveRowLocally,
    applyRemoteMove,
    effectiveSort,
    rowHeight,
    pinnedWidth,
    widthOf,
    resetLayout,
    pinnedKeys,
    isPinned,
    isPinnedRight,
    pinRightColumn,
    canPinMore,
    maxPinned: MAX_PINNED,
    pinColumn,
    unpinColumn,
    pinnedWidthWith,
    areaOf,
    moveColumn,
    allColumns: GRID_COLUMNS,
    // состояние ячеек
    // действия
    load,
    loadMore,
    loadAllRows,
    reload,
    setSort,
    setFilter,
    clearFilters,
    showHiddenRows,
    leaveHiddenRows,
    setRowsHidden,
    revealHiddenGap,
    patchCell,
    patchWorkObject,
    replaceRow,
    applyRemoteCell,
    removeRow,
    prependRow,
    rowById,
    bulkUpdate,
    bulkDelete,
    restoreRows,
    colorRows,
    setCellsBold,
    colorCells,
    restoreCellColors,
    pasteBlock,
    createCandidate,
    insertCandidate,
    checkDuplicates,
    checkBlacklist,
    checkComments,
    logCopy,
  };
});

export interface PasteResult {
  historyRows: CandidateHistoryRow[];
  created: number;
  updated: number;
  skipped: number;
  createdIds: string[];
  updatedIds: string[];
  issues: Array<{ row: number; column?: string; message: string }>;
}
